/**
 * Message catalog. Keys are stable; the three vernaculars are values
 * (EN / ES / PT-BR — the family standard). A missing key falls back to the
 * English string, then to the key itself, so a partial catalog never blanks.
 */
import type { Locale } from "./locales";

export type MessageKey =
  // shell chrome
  | "app.name"
  | "topbar.wellSwitcher"
  | "topbar.theme"
  | "topbar.theme.dark"
  | "topbar.theme.light"
  | "topbar.theme.auto"
  | "topbar.locale"
  | "topbar.lineage"
  | "topbar.lineage.adminOnly"
  | "topbar.lineage.on"
  | "topbar.lineage.off"
  | "settings.title"
  | "settings.open"
  | "settings.tenant"
  | "settings.units"
  | "settings.units.auto"
  | "settings.units.hint"
  | "settings.currency"
  | "settings.currency.native"
  | "nav.collapse"
  | "nav.expand"
  | "well.switcher.title"
  | "well.switcher.all"
  // nav (W0–W9 sitemap)
  | "nav.finder"
  | "nav.workspace"
  | "nav.holePlan"
  | "nav.casing"
  | "nav.cost"
  | "nav.program"
  | "nav.trajectory"
  | "nav.timeline"
  | "nav.versions"
  | "nav.publish"
  // event letters
  | "letter.P"
  | "letter.T"
  | "letter.L"
  | "letter.W"
  | "letter.S"
  | "conv.yes"
  | "conv.no"
  | "conv.convention"
  | "conv.unconvention"
  // plan status
  | "plan.status.none"
  | "plan.status.draft"
  | "plan.status.review"
  | "plan.status.approved"
  | "plan.status.locked"
  | "plan.status.published"
  // W0 — finder
  | "finder.title"
  | "finder.badge"
  | "finder.lede"
  | "finder.newPlan"
  | "finder.filter.basin"
  | "finder.filter.field"
  | "finder.filter.pad"
  | "finder.filter.well"
  | "finder.filter.allBasin"
  | "finder.filter.allField"
  | "finder.filter.allPad"
  | "finder.filter.wellPlaceholder"
  | "finder.filter.eventType"
  | "finder.clear"
  | "finder.clearAll"
  | "finder.count.wells"
  | "finder.count.events"
  | "finder.col.well"
  | "finder.col.location"
  | "finder.col.pad"
  | "finder.col.events"
  | "finder.col.planStatus"
  | "finder.col.actions"
  | "finder.empty"
  | "finder.action.open"
  | "finder.action.newPlan"
  | "finder.action.clone"
  | "finder.action.newVersion"
  | "finder.source"
  // W0 — new plan wizard
  | "wizard.title"
  | "wizard.step.well"
  | "wizard.step.eventType"
  | "wizard.step.confirm"
  | "wizard.searchWell"
  | "wizard.searchWellPlaceholder"
  | "wizard.pickWell"
  | "wizard.noMatch"
  | "wizard.chosenWell"
  | "wizard.eventTypePrompt"
  | "wizard.note"
  | "wizard.sum.well"
  | "wizard.sum.uwi"
  | "wizard.sum.eventType"
  | "wizard.sum.conv"
  | "wizard.sum.afe"
  | "wizard.sum.afeAuto"
  | "wizard.back"
  | "wizard.cancel"
  | "wizard.next"
  | "wizard.create"
  | "wizard.creating"
  // W4 — cost plan / AFE lines
  | "cost.title"
  | "cost.lede"
  | "cost.boundary"
  | "cost.save"
  | "cost.saving"
  | "cost.addLine"
  | "cost.empty"
  | "cost.ctx.well"
  | "cost.ctx.afe"
  | "cost.ctx.total"
  | "cost.rail.title"
  | "cost.rail.hint"
  | "cost.step.all"
  | "cost.step.allMeta"
  | "cost.step.global"
  | "cost.grid.title"
  | "cost.grid.subAll"
  | "cost.col.account"
  | "cost.col.description"
  | "cost.col.programStep"
  | "cost.col.accrual"
  | "cost.col.unit"
  | "cost.col.qty"
  | "cost.col.rate"
  | "cost.col.budget"
  | "cost.account.placeholder"
  | "cost.unit.placeholder"
  | "cost.budget.derived"
  | "cost.total.all"
  | "cost.total.step"
  | "cost.phase.title"
  | "cost.accrual.avance_etapas"
  | "cost.accrual.devengado"
  | "cost.accrual.adjusted_cost"
  | "cost.accrual.straight_line"
  // W5 — operative program
  | "program.title"
  | "program.badge"
  | "program.save"
  | "program.saving"
  | "program.note"
  | "program.ctx.well"
  | "program.ctx.event"
  | "program.ctx.afe"
  | "program.kpi.phases"
  | "program.kpi.totalTime"
  | "program.kpi.dol"
  | "program.kpi.finalDepth"
  | "program.kpi.amount"
  | "program.grid.title"
  | "program.grid.hint"
  | "program.addPhase"
  | "program.empty"
  | "program.total"
  | "program.accHint"
  | "program.phasePlaceholder"
  | "program.descPlaceholder"
  | "program.col.seq"
  | "program.col.phase"
  | "program.col.description"
  | "program.col.depthFrom"
  | "program.col.depthUntil"
  | "program.col.time"
  | "program.col.dol"
  | "program.col.pct"
  | "program.col.amount"
  | "program.col.accTime"
  | "program.curve.title"
  | "program.curve.sub"
  | "program.curve.xLabel"
  | "program.curve.yLabel"
  | "program.curve.aria"
  | "program.curve.legend"
  | "program.footnote"
  // W6 — trajectory plan
  | "traj.title"
  | "traj.badge"
  | "traj.save"
  | "traj.computing"
  | "traj.note"
  | "traj.computeHint"
  | "traj.computed"
  | "traj.kop"
  | "traj.ctx.well"
  | "traj.ctx.event"
  | "traj.ctx.afe"
  | "traj.kpi.stations"
  | "traj.kpi.finalMd"
  | "traj.kpi.targetTvd"
  | "traj.kpi.kop"
  | "traj.kpi.displacement"
  | "traj.kpi.maxDls"
  | "traj.grid.title"
  | "traj.grid.hint"
  | "traj.addStation"
  | "traj.empty"
  | "traj.col.segment"
  | "traj.col.md"
  | "traj.col.inc"
  | "traj.col.azi"
  | "traj.col.tvd"
  | "traj.col.ns"
  | "traj.col.ew"
  | "traj.col.dls"
  | "traj.seg.vertical"
  | "traj.seg.build"
  | "traj.seg.lateral"
  | "traj.section.title"
  | "traj.section.sub"
  | "traj.section.xLabel"
  | "traj.section.yLabel"
  | "traj.section.aria"
  | "traj.section.empty"
  | "traj.plan.title"
  | "traj.plan.sub"
  | "traj.plan.xLabel"
  | "traj.plan.yLabel"
  | "traj.plan.aria"
  | "traj.plan.empty"
  | "traj.na.title"
  | "traj.na.body"
  | "traj.footnote"
  // W2 — hole plan designer
  | "holePlan.title"
  | "holePlan.badge"
  | "holePlan.copyPrior"
  | "holePlan.save"
  | "holePlan.saving"
  | "holePlan.savedAt"
  | "holePlan.note"
  | "holePlan.gridTitle"
  | "holePlan.gridHint"
  | "holePlan.footHint"
  | "holePlan.addSection"
  | "holePlan.empty"
  | "holePlan.noPlan"
  | "holePlan.unnamed"
  | "holePlan.selectHint"
  | "holePlan.selectFirst"
  | "holePlan.diaCatalog"
  | "holePlan.schematic"
  | "holePlan.schematicAria"
  | "holePlan.kpi.sections"
  | "holePlan.kpi.td"
  | "holePlan.kpi.dol"
  | "holePlan.kpi.cost"
  | "holePlan.col.phase"
  | "holePlan.col.section"
  | "holePlan.col.diameter"
  | "holePlan.col.topMd"
  | "holePlan.col.baseMd"
  | "holePlan.col.length"
  | "holePlan.col.dol"
  | "holePlan.col.targetDuration"
  | "holePlan.col.targetCost"
  | "holePlan.ctx.well"
  | "holePlan.ctx.event"
  | "holePlan.ctx.afe"
  | "holePlan.na.title"
  | "holePlan.na.body"
  | "holePlan.na.toProgram"
  | "holePlan.na.toCost"
  // W3 — casing & tubular design
  | "casing.title"
  | "casing.badge"
  | "casing.importHole"
  | "casing.save"
  | "casing.saving"
  | "casing.savedAt"
  | "casing.note"
  | "casing.gridTitle"
  | "casing.gridHint"
  | "casing.addString"
  | "casing.empty"
  | "casing.noPlan"
  | "casing.unnamed"
  | "casing.selectHint"
  | "casing.schematic"
  | "casing.schematicAria"
  | "casing.ok"
  | "casing.od.placeholder"
  | "casing.type.choose"
  | "casing.type.conductor"
  | "casing.type.surface"
  | "casing.type.intermediate"
  | "casing.type.production"
  | "casing.kpi.strings"
  | "casing.kpi.deepestShoe"
  | "casing.kpi.productionOd"
  | "casing.kpi.warnings"
  | "casing.col.string"
  | "casing.col.type"
  | "casing.col.section"
  | "casing.col.hole"
  | "casing.col.od"
  | "casing.col.top"
  | "casing.col.base"
  | "casing.col.length"
  | "casing.col.nesting"
  | "casing.warn.od_ge_hole"
  | "casing.warn.od_ge_prev"
  | "casing.warn.base_le_prev"
  | "casing.nest.warnTitle"
  | "casing.nest.warnBody"
  | "casing.nest.rule"
  | "casing.ctx.well"
  | "casing.ctx.event"
  | "casing.ctx.afe"
  | "casing.na.title"
  | "casing.na.body"
  | "casing.na.toProgram"
  | "casing.na.toCost"
  // W1 — plan workspace / AFE hub
  | "ws.title"
  | "ws.badge"
  | "ws.publish"
  | "ws.empty"
  | "ws.ctx.well"
  | "ws.ctx.event"
  | "ws.ctx.afe"
  | "ws.note"
  | "ws.kpi.duration"
  | "ws.kpi.cost"
  | "ws.kpi.finalMd"
  | "ws.kpi.rig"
  | "ws.tabs.aria"
  | "ws.tab.objetivo"
  | "ws.tab.programa"
  | "ws.tab.holePlan"
  | "ws.tab.casing"
  | "ws.tab.cost"
  | "ws.tab.atributos"
  | "ws.tab.fractura"
  | "ws.objetivo.title"
  | "ws.objetivo.empty"
  | "ws.atributos.title"
  | "ws.atributos.empty"
  | "ws.switcher.aria"
  | "ws.switcher.heading"
  | "ws.curve.title"
  | "ws.curve.sub"
  | "ws.curve.aria"
  | "ws.curve.xAxis"
  | "ws.curve.yAxis"
  | "ws.curve.series"
  | "ws.version.title"
  | "ws.version.editedBy"
  | "ws.version.count"
  | "ws.version.none"
  | "ws.version.history"
  | "ws.publishCard.title"
  | "ws.publishCard.body"
  | "ws.publishCard.action"
  // W9 — publish / handoff
  | "publish.title"
  | "publish.badge"
  | "publish.note"
  | "publish.ctx.well"
  | "publish.ctx.event"
  | "publish.ctx.afe"
  | "publish.checklist.title"
  | "publish.count.ok"
  | "publish.count.warn"
  | "publish.count.blocking"
  | "publish.sev.ok"
  | "publish.sev.warn"
  | "publish.sev.blocking"
  | "publish.fix"
  | "publish.check.requiredFields"
  | "publish.check.requiredFields.ok"
  | "publish.check.tvdLeMd"
  | "publish.check.tvdLeMd.ok"
  | "publish.check.monotonic"
  | "publish.check.monotonic.ok"
  | "publish.check.costBalance"
  | "publish.check.costBalance.ok"
  | "publish.check.casingNesting"
  | "publish.check.casingNesting.ok"
  | "publish.check.programSeq"
  | "publish.check.programSeq.ok"
  | "publish.diff.title"
  | "publish.diff.sub"
  | "publish.diff.first"
  | "publish.diff.none"
  | "publish.rail.title"
  | "publish.rail.hint"
  | "publish.action"
  | "publish.publishing"
  | "publish.published"
  | "publish.error"
  | "publish.gate.blocked"
  | "publish.gate.notReady"
  | "publish.gate.notApproved"
  | "publish.gate.ready"
  | "publish.done"
  | "publish.done.z"
  | "publish.done.aura"
  | "publish.targets.title"
  | "publish.target.z"
  | "publish.target.z.sub"
  | "publish.target.aura"
  | "publish.target.aura.sub"
  // W8 — versions & supplements
  | "versions.title"
  | "versions.badge"
  | "versions.empty"
  | "versions.noVersions"
  | "versions.ctx.well"
  | "versions.ctx.event"
  | "versions.ctx.afe"
  | "versions.note"
  | "versions.kpi.original"
  | "versions.kpi.approved"
  | "versions.kpi.pending"
  | "versions.kpi.authorized"
  | "versions.rollup.original"
  | "versions.rollup.approved"
  | "versions.rollup.authorized"
  | "versions.history.title"
  | "versions.history.count"
  | "versions.history.publishHint"
  | "versions.publish.ready"
  | "versions.publish.notReady"
  | "versions.item.published"
  | "versions.diff.title"
  | "versions.diff.sub"
  | "versions.diff.chip"
  | "versions.diff.initial"
  | "versions.diff.field"
  | "versions.diff.before"
  | "versions.diff.after"
  | "versions.diff.programa"
  | "versions.diff.costo"
  | "versions.diff.targets"
  | "versions.diff.phases"
  | "versions.diff.days"
  | "versions.diff.afeTotal"
  | "versions.diff.note"
  | "versions.supp.title"
  | "versions.supp.request"
  | "versions.supp.lockedHint"
  | "versions.supp.lockedMsg"
  | "versions.supp.empty"
  | "versions.supp.col.seq"
  | "versions.supp.col.reason"
  | "versions.supp.col.amount"
  | "versions.supp.col.by"
  | "versions.supp.col.status"
  | "versions.supp.col.date"
  | "versions.supp.state.requested"
  | "versions.supp.state.approved"
  | "versions.supp.state.rejected"
  | "versions.supp.sumApproved"
  | "versions.supp.authorizedNote"
  | "versions.supp.formTitle"
  | "versions.supp.formSub"
  | "versions.supp.f.reason"
  | "versions.supp.f.reasonPh"
  | "versions.supp.f.amount"
  | "versions.supp.f.projected"
  | "versions.supp.f.hint"
  | "versions.supp.f.send"
  | "versions.supp.f.sending"
  | "versions.trail.title"
  | "versions.trail.empty"
  | "versions.trail.chip"
  | "versions.trail.sub"
  | "versions.trail.stage.field"
  | "versions.trail.stage.office"
  | "versions.trail.stage.jv"
  | "versions.trail.state.approved"
  | "versions.trail.state.pending"
  | "versions.trail.state.locked"
  | "versions.trail.state.rejected"
  | "versions.trail.approve"
  | "versions.trail.reject"
  // placeholder screens
  | "placeholder.subtitle"
  | "placeholder.comingSoon"
  // common
  | "common.loading"
  | "common.error"
  | "common.retry"
  | "common.na"
  | "common.of"
  | "common.close"
  // W7 — plan timeline (editor)
  | "timeline.title"
  | "timeline.badge"
  | "timeline.save"
  | "timeline.saving"
  | "timeline.note"
  | "timeline.ctx.well"
  | "timeline.ctx.event"
  | "timeline.zoom"
  | "timeline.zoom.day"
  | "timeline.zoom.week"
  | "timeline.zoom.month"
  | "timeline.show"
  | "timeline.pa.both"
  | "timeline.pa.plan"
  | "timeline.pa.actual"
  | "timeline.todayLine"
  | "timeline.opFilter"
  | "timeline.operations"
  | "timeline.op.pad"
  | "timeline.op.drilling"
  | "timeline.op.unconv"
  | "timeline.op.completion"
  | "timeline.op.wireline"
  | "timeline.op.ct"
  | "timeline.op.workover"
  | "timeline.legend.plan"
  | "timeline.legend.actual"
  | "timeline.legend.npt"
  | "timeline.legend.milestone"
  | "timeline.legend.lookahead"
  | "timeline.canvasAria"
  | "timeline.empty"
  | "timeline.kind.cpe"
  | "timeline.kind.well"
  | "timeline.kind.service"
  | "timeline.kind.pad"
  | "timeline.grid.title"
  | "timeline.grid.hint"
  | "timeline.addStep"
  | "timeline.trackEmpty"
  | "timeline.selectRow"
  | "timeline.labelPlaceholder"
  | "timeline.phasePlaceholder"
  | "timeline.col.seq"
  | "timeline.col.label"
  | "timeline.col.start"
  | "timeline.col.end"
  | "timeline.col.phase"
  | "timeline.editor.title"
  | "timeline.editor.hint"
  | "timeline.editor.readonly"
  | "timeline.editor.label"
  | "timeline.editor.start"
  | "timeline.editor.end"
  | "timeline.editor.depthFrom"
  | "timeline.editor.depthUntil"
  | "timeline.editor.cpe"
  | "timeline.editor.phase"
  | "timeline.editor.delete"
  | "timeline.kpi.planBars"
  | "timeline.kpi.tracks"
  | "timeline.kpi.lookahead"
  | "timeline.kpi.weeksUnit"
  | "timeline.footnote"
  | "common.cancel";

type Catalog = Record<MessageKey, string>;

const en: Catalog = {
  "app.name": "Oxbo-W",
  "topbar.wellSwitcher": "Switch well / pad",
  "topbar.theme": "Theme",
  "topbar.theme.dark": "Dark",
  "topbar.theme.light": "Light",
  "topbar.theme.auto": "Auto",
  "topbar.locale": "Language",
  "topbar.lineage": "Data lineage",
  "topbar.lineage.adminOnly": "admin only",
  "topbar.lineage.on": "On",
  "topbar.lineage.off": "Off",
  "settings.title": "Settings",
  "settings.open": "Open settings",
  "settings.tenant": "Workspace",
  "settings.units": "Units",
  "settings.units.auto": "Auto (default)",
  "settings.units.hint": "Convert every measurement to your preferred unit system.",
  "settings.currency": "Currency",
  "settings.currency.native": "Native",
  "nav.collapse": "Collapse menu",
  "nav.expand": "Expand menu",
  "well.switcher.title": "Wells",
  "well.switcher.all": "All wells",
  "nav.finder": "Plan finder",
  "nav.workspace": "Plan workspace",
  "nav.holePlan": "Hole plan",
  "nav.casing": "Casing & tubulars",
  "nav.cost": "Cost / AFE lines",
  "nav.program": "Operative program",
  "nav.trajectory": "Trajectory plan",
  "nav.timeline": "Plan timeline",
  "nav.versions": "Versions & supplements",
  "nav.publish": "Publish / handoff",
  "letter.P": "Drilling",
  "letter.T": "Completion",
  "letter.L": "Pulling",
  "letter.W": "Workover",
  "letter.S": "Sidetrack",
  "conv.yes": "Conv.",
  "conv.no": "Unconv.",
  "conv.convention": "Conventional",
  "conv.unconvention": "Unconventional",
  "plan.status.none": "No plan",
  "plan.status.draft": "Draft v{n}",
  "plan.status.review": "In review v{n}",
  "plan.status.approved": "Approved v{n}",
  "plan.status.locked": "Locked v{n}",
  "plan.status.published": "Published v{n}",
  "finder.title": "Plan finder",
  "finder.badge": "Entry point · Oxbo-W",
  "finder.lede":
    "Find a well and event to open its plan, or start a new one. A well can carry drilling, completion, workover and pulling events at once — each with its own plan and version.",
  "finder.newPlan": "New plan",
  "finder.filter.basin": "Basin",
  "finder.filter.field": "Field",
  "finder.filter.pad": "Pad",
  "finder.filter.well": "Well / UWI",
  "finder.filter.allBasin": "All basins",
  "finder.filter.allField": "All fields",
  "finder.filter.allPad": "All pads",
  "finder.filter.wellPlaceholder": "Search by well name or UWI…",
  "finder.filter.eventType": "Event type",
  "finder.clear": "Clear",
  "finder.clearAll": "Clear all filters",
  "finder.count.wells": "wells",
  "finder.count.events": "events",
  "finder.col.well": "Well",
  "finder.col.location": "Basin / Field",
  "finder.col.pad": "Pad",
  "finder.col.events": "Events",
  "finder.col.planStatus": "Plan status",
  "finder.col.actions": "Actions",
  "finder.empty": "No results for the current filters.",
  "finder.action.open": "Open",
  "finder.action.newPlan": "New plan",
  "finder.action.clone": "Clone plan",
  "finder.action.newVersion": "New version",
  "finder.source":
    "Data: Oxbo-M well/wellbore register + event_codes (letter + conventional) · Oxbo-P plan of record. Event letter via tipo_evento_enrichment — same map as Aura AFE-Design.",
  "wizard.title": "New plan",
  "wizard.step.well": "Well",
  "wizard.step.eventType": "Event type",
  "wizard.step.confirm": "Confirm",
  "wizard.searchWell": "Search well",
  "wizard.searchWellPlaceholder": "Well name, UWI, pad or field…",
  "wizard.pickWell": "Select well",
  "wizard.noMatch": "No matches.",
  "wizard.chosenWell": "Well",
  "wizard.eventTypePrompt": "Event type — select and confirm conventional / unconventional",
  "wizard.note":
    "A plan will be created in Draft v1. You can edit objective, program, hole plan, casing and cost in the Plan Workspace immediately.",
  "wizard.sum.well": "Well",
  "wizard.sum.uwi": "UWI",
  "wizard.sum.eventType": "Event type",
  "wizard.sum.conv": "Conventional / Unconv.",
  "wizard.sum.afe": "AFE (assigned on create)",
  "wizard.sum.afeAuto": "generated on create",
  "wizard.back": "Back",
  "wizard.cancel": "Cancel",
  "wizard.next": "Continue",
  "wizard.create": "Create plan (Draft v1)",
  "wizard.creating": "Creating…",
  "cost.title": "Cost plan · AFE lines",
  "cost.lede":
    "Authoring screen — editable WBS (program step) × CBS (account) grid. Changes are held per line; publishing the plan sends it to Oxbo-Z as the baseline.",
  "cost.boundary":
    "Oxbo-W authors the PLAN: budget, accrual method, unit, planned qty and rate. Committed, accrued (devengado) and actual come from the accrual engine and are read-only in Oxbo-Z — they are not editable here.",
  "cost.save": "Save changes",
  "cost.saving": "Saving…",
  "cost.addLine": "+ Add AFE line",
  "cost.empty": "No AFE lines yet — use “Add AFE line” to start.",
  "cost.ctx.well": "Well",
  "cost.ctx.afe": "AFE",
  "cost.ctx.total": "authorized total",
  "cost.rail.title": "Program sequence",
  "cost.rail.hint": "Select a step to filter the AFE lines of that stage.",
  "cost.step.all": "All steps",
  "cost.step.allMeta": "{n} lines",
  "cost.step.global": "Global (no step)",
  "cost.grid.title": "AFE lines",
  "cost.grid.subAll": "— all program steps",
  "cost.col.account": "Account (CoA · Oxbo-M)",
  "cost.col.description": "Description",
  "cost.col.programStep": "Program step",
  "cost.col.accrual": "Accrual method",
  "cost.col.unit": "Unit",
  "cost.col.qty": "Qty",
  "cost.col.rate": "Rate",
  "cost.col.budget": "Budget",
  "cost.account.placeholder": "Account…",
  "cost.unit.placeholder": "un",
  "cost.budget.derived": "Budget = planned qty × rate (server-authoritative)",
  "cost.total.all": "Total — all lines",
  "cost.total.step": "Total — this step",
  "cost.phase.title": "Cost by phase (AFE budget)",
  "cost.accrual.avance_etapas": "By stage (avance)",
  "cost.accrual.devengado": "Accrued (devengado)",
  "cost.accrual.adjusted_cost": "Adjusted cost",
  "cost.accrual.straight_line": "Straight line",
  "program.title": "Operative program",
  "program.badge": "Authoring · Drilling engineer / Planner",
  "program.save": "Save program",
  "program.saving": "Saving…",
  "program.note":
    "This is the operative program — the ordered sequence of phases that governs the plan curve (depth vs. days) read by Oxbo-Z and the reconciler. Reorder to change the day axis; the accumulated time and the curve recompute on every change.",
  "program.ctx.well": "Well",
  "program.ctx.event": "Event",
  "program.ctx.afe": "AFE",
  "program.kpi.phases": "Phases in sequence",
  "program.kpi.totalTime": "Total time (acc.)",
  "program.kpi.dol": "Total DOL",
  "program.kpi.finalDepth": "Final depth (MD)",
  "program.kpi.amount": "Total amount",
  "program.grid.title": "Phase sequence",
  "program.grid.hint": "1..N, reorder with ↑ / ↓",
  "program.addPhase": "Add phase",
  "program.empty": "No phases yet — add the first phase to build the plan curve.",
  "program.total": "Total",
  "program.accHint":
    "Acc. time (h) is the running sum of Time (h) in Sequence order — the X axis of the plan curve (÷24 = elapsed days). Editing Time, DOL or Depth until updates the curve below.",
  "program.phasePlaceholder": "Phase…",
  "program.descPlaceholder": "Activity description…",
  "program.col.seq": "Seq.",
  "program.col.phase": "Phase",
  "program.col.description": "Description / activity code",
  "program.col.depthFrom": "Depth from (m)",
  "program.col.depthUntil": "Depth until (m)",
  "program.col.time": "Time (h)",
  "program.col.dol": "DOL (d)",
  "program.col.pct": "% acc. progress",
  "program.col.amount": "Amount (US$)",
  "program.col.accTime": "Acc. time (h)",
  "program.curve.title": "Plan curve — depth vs. days",
  "program.curve.sub": "Derives from Sequence × acc. time (h)/24 × Depth until",
  "program.curve.xLabel": "Elapsed days (acc. time h ÷ 24)",
  "program.curve.yLabel": "Measured depth (m)",
  "program.curve.aria":
    "Plan curve showing accumulated measured depth against elapsed days, one vertex per phase in sequence order.",
  "program.curve.legend": "Plan curve",
  "program.footnote":
    "The operative program adapts to the event type (drilling / completion / workover). For completion events the depth axis may be flat or fall in some phases (perforating, flowback) — the curve stays monotonic on the days axis, not necessarily in net depth when the phase does not drill.",
  // W6 — trajectory plan
  "traj.title": "Trajectory plan",
  "traj.badge": "Authoring · Directional engineer / Planner",
  "traj.save": "Save + compute",
  "traj.computing": "Computing…",
  "traj.note":
    "Planned survey stations — MD · inclination · azimuth are editable; TVD · N/S · E/W · DLS are computed by minimum curvature on the server (read-only, provenance = computed). Edits clear the computed columns until the next save re-computes them.",
  "traj.computeHint":
    "Method: minimum curvature between consecutive stations. The server returns TVD / northing / easting / DLS; the two views + KPIs redraw from what it returns.",
  "traj.computed": "computed",
  "traj.kop": "KOP",
  "traj.ctx.well": "Well",
  "traj.ctx.event": "Event",
  "traj.ctx.afe": "AFE",
  "traj.kpi.stations": "Survey stations",
  "traj.kpi.finalMd": "Final MD (planned)",
  "traj.kpi.targetTvd": "Target TVD",
  "traj.kpi.kop": "KOP (curve start)",
  "traj.kpi.displacement": "Lateral displacement (horiz.)",
  "traj.kpi.maxDls": "Max planned DLS",
  "traj.grid.title": "Planned survey stations",
  "traj.grid.hint": "vertical → build → lateral, ordered by MD",
  "traj.addStation": "Add survey station",
  "traj.empty": "No stations yet — add the surface station (MD 0) to start the plan.",
  "traj.col.segment": "Segment",
  "traj.col.md": "MD (m)",
  "traj.col.inc": "Inclination (°)",
  "traj.col.azi": "Azimuth (°)",
  "traj.col.tvd": "TVD (m)",
  "traj.col.ns": "N/S (m)",
  "traj.col.ew": "E/W (m)",
  "traj.col.dls": "DLS (°/30m)",
  "traj.seg.vertical": "Vertical",
  "traj.seg.build": "Build (curve)",
  "traj.seg.lateral": "Lateral",
  "traj.section.title": "Vertical section",
  "traj.section.sub": "TVD vs. horizontal displacement",
  "traj.section.xLabel": "Horizontal displacement (m)",
  "traj.section.yLabel": "TVD (m)",
  "traj.section.aria":
    "Vertical section of the well — true vertical depth against horizontal displacement, coloured by segment (vertical, build, lateral).",
  "traj.section.empty": "No computed TVD yet — save the stations to compute the section.",
  "traj.plan.title": "Plan view — N/S vs. E/W",
  "traj.plan.sub": "bird's-eye, north up",
  "traj.plan.xLabel": "E/W (m)",
  "traj.plan.yLabel": "N/S (m)",
  "traj.plan.aria":
    "Plan (bird's-eye) view of the well — northing against easting from the wellhead at origin, coloured by segment.",
  "traj.plan.empty":
    "No computed northing / easting yet — save the stations to compute the plan view.",
  "traj.na.title": "Trajectory does not apply to this event",
  "traj.na.body":
    "A survey trajectory is authored for events that drill a wellbore (drilling / sidetrack). Lighter events (pulling / workover / coiled tubing) carry no trajectory plan.",
  "traj.footnote":
    "The plan trajectory is the baseline Oxbo-Z reads for actual-vs-plan (survey MWD/Gyro passes overlay against it once drilling starts). Minimum-curvature computation is server-authoritative so W's plan and Z's actuals meet to the number.",
  "holePlan.title": "Hole plan designer",
  "holePlan.badge": "Authoring · Drilling engineer / Planner",
  "holePlan.copyPrior": "Copy from prior plan",
  "holePlan.save": "Save hole plan",
  "holePlan.saving": "Saving…",
  "holePlan.savedAt": "Saved {time}",
  "holePlan.note":
    "The hole plan defines the drilled sections by diameter and MD interval; it feeds the casing design (W3), the operative program (W5) and the plan curve. Changes stay in the draft version until Publish (W9).",
  "holePlan.gridTitle": "Hole plan sections",
  "holePlan.gridHint":
    "Reorder rows with the ↑ ↓ controls. Click a section to highlight it in the schematic. Diameter can be taken from the Oxbo-M bit / hole-size catalog.",
  "holePlan.footHint":
    "Each section's Base MD should equal the next section's Top MD. Length is computed automatically (Base − Top).",
  "holePlan.addSection": "+ Add section",
  "holePlan.empty": "No sections yet — add the first drilled section.",
  "holePlan.noPlan": "No plan selected. Open a plan from the finder first.",
  "holePlan.unnamed": "Unnamed section",
  "holePlan.selectHint": "Highlight this section in the schematic",
  "holePlan.selectFirst": "Select a section row first",
  "holePlan.diaCatalog": "Diameter catalog (Oxbo-M)",
  "holePlan.schematic": "Well schematic",
  "holePlan.schematicAria":
    "Vertical well schematic showing the telescoping hole sections from the widest conductor at surface down to the narrowest section at total depth, with casing shoes at each section base.",
  "holePlan.kpi.sections": "Sections",
  "holePlan.kpi.td": "Total MD (TD)",
  "holePlan.kpi.dol": "Cumulative DOL",
  "holePlan.kpi.cost": "Total target cost",
  "holePlan.col.phase": "Phase",
  "holePlan.col.section": "Section",
  "holePlan.col.diameter": "Diameter (in)",
  "holePlan.col.topMd": "Top MD (m)",
  "holePlan.col.baseMd": "Base MD (m)",
  "holePlan.col.length": "Length (m)",
  "holePlan.col.dol": "DOL (d)",
  "holePlan.col.targetDuration": "Target dur. (d)",
  "holePlan.col.targetCost": "Target cost (US$)",
  "holePlan.ctx.well": "Well",
  "holePlan.ctx.event": "Event",
  "holePlan.ctx.afe": "AFE",
  "holePlan.na.title": "The hole plan does not apply to this event",
  "holePlan.na.body":
    "The selected event ({code}) reuses the existing drilled well and needs no hole-section design. This plan uses the operative program (W5) and cost plan (W4) instead.",
  "holePlan.na.toProgram": "Go to operative program",
  "holePlan.na.toCost": "Go to cost plan",
  "casing.title": "Casing & tubular design",
  "casing.badge": "Authoring · Drilling engineer / Planner",
  "casing.importHole": "Import from hole plan",
  "casing.save": "Save casing",
  "casing.saving": "Saving…",
  "casing.savedAt": "Saved {time}",
  "casing.note":
    "The casing design places a tubular string in each drilled section — outermost Conductor at surface down to the innermost Production string at TD. Casing OD binds to the Oxbo-M tubular catalog; nesting is validated as you edit. Changes stay in the draft version until Publish (W9).",
  "casing.gridTitle": "Casing strings",
  "casing.gridHint":
    "Order runs Conductor → Surface → Intermediate → Production (outermost to innermost). Reorder rows with the ↑ ↓ controls. Click a string to highlight it in the schematic. Casing OD is taken from the Oxbo-M tubular catalog.",
  "casing.addString": "+ Add string",
  "casing.empty": "No casing strings yet — add the first string (usually the Conductor).",
  "casing.noPlan": "No plan selected. Open a plan from the finder first.",
  "casing.unnamed": "Unnamed string",
  "casing.selectHint": "Highlight this string in the schematic",
  "casing.schematic": "Casing schematic",
  "casing.schematicAria":
    "Concentric casing schematic showing the nested tubular strings from the widest conductor at surface down to the narrowest production string at total depth, with a casing shoe at each string base.",
  "casing.ok": "OK",
  "casing.od.placeholder": "Tubular…",
  "casing.type.choose": "Type…",
  "casing.type.conductor": "Conductor",
  "casing.type.surface": "Surface",
  "casing.type.intermediate": "Intermediate",
  "casing.type.production": "Production",
  "casing.kpi.strings": "Strings designed",
  "casing.kpi.deepestShoe": "Deepest shoe",
  "casing.kpi.productionOd": "Production casing",
  "casing.kpi.warnings": "Nesting warnings",
  "casing.col.string": "String",
  "casing.col.type": "Type",
  "casing.col.section": "Section",
  "casing.col.hole": "Hole (in)",
  "casing.col.od": "Casing OD (in)",
  "casing.col.top": "Top (m)",
  "casing.col.base": "Base (m)",
  "casing.col.length": "Length (m)",
  "casing.col.nesting": "Nesting",
  "casing.warn.od_ge_hole": "OD ≥ hole",
  "casing.warn.od_ge_prev": "OD ≥ prior string",
  "casing.warn.base_le_prev": "Base ≤ prior string",
  "casing.nest.warnTitle": "Inconsistent nesting in {name}:",
  "casing.nest.warnBody":
    "the selected casing OD does not fit inside its own section's hole, or does not nest inside the string above. Adjust the tubular or the hole diameter before publishing.",
  "casing.nest.rule":
    "Nesting rule: for each string, Casing OD < its section's Hole < the previous string's Casing OD, and each string reaches deeper than the one above. Conductor (outermost) → Production (innermost).",
  "casing.ctx.well": "Well",
  "casing.ctx.event": "Event",
  "casing.ctx.afe": "AFE",
  "casing.na.title": "Casing design does not apply to this event",
  "casing.na.body":
    "The selected event ({code}) does not run casing and needs no tubular design. This plan uses the operative program (W5) and cost plan (W4) instead.",
  "casing.na.toProgram": "Go to operative program",
  "casing.na.toCost": "Go to cost plan",
  "ws.title": "Plan workspace",
  "ws.badge": "Authoring · Drilling engineer / Planner",
  "ws.publish": "Publish",
  "ws.empty": "No event selected. Open a well and event from the Plan finder to author its plan.",
  "ws.ctx.well": "Well",
  "ws.ctx.event": "Event",
  "ws.ctx.afe": "AFE",
  "ws.note":
    "This workspace is authoring. Objective and attributes edit here; the program, hole plan, casing, cost and trajectory open in their own screens. Changes stay in the draft version until Publish sends the baseline to Oxbo-Z and emits secuencia_operativa to Aura.",
  "ws.kpi.duration": "Target duration",
  "ws.kpi.cost": "Target cost",
  "ws.kpi.finalMd": "Final depth (MD)",
  "ws.kpi.rig": "Planned rig",
  "ws.tabs.aria": "Plan design datasets",
  "ws.tab.objetivo": "Objective",
  "ws.tab.programa": "Program",
  "ws.tab.holePlan": "Hole plan",
  "ws.tab.casing": "Casing",
  "ws.tab.cost": "Cost plan",
  "ws.tab.atributos": "Attributes",
  "ws.tab.fractura": "Frac / Stages",
  "ws.objetivo.title": "Planned objective",
  "ws.objetivo.empty": "No objective attributes yet.",
  "ws.atributos.title": "Attributes",
  "ws.atributos.empty": "No attributes yet.",
  "ws.switcher.aria": "Switch event / plan",
  "ws.switcher.heading": "{well} · events on this well",
  "ws.curve.title": "Plan curve — depth vs. days",
  "ws.curve.sub": "Cumulative MD (m) by phase",
  "ws.curve.aria":
    "Plan curve showing cumulative measured depth against elapsed days, one marker per program phase.",
  "ws.curve.xAxis": "Elapsed days (cumulative DOL)",
  "ws.curve.yAxis": "MD (m)",
  "ws.curve.series": "Depth plan (current version)",
  "ws.version.title": "Current version",
  "ws.version.editedBy": "Edited by {actor}",
  "ws.version.count": "{n} version(s) on record",
  "ws.version.none": "No plan yet on this event — start one from the Plan finder.",
  "ws.version.history": "Version history",
  "ws.publishCard.title": "Publish plan of record",
  "ws.publishCard.body":
    "Publishing sends this version as the baseline to Oxbo-Z (plan-vs-actual) and emits the secuencia_operativa payload to Aura (AFE / program / cost / hole plan / casing / attributes).",
  "ws.publishCard.action": "Publish to Oxbo-Z + Aura",
  "publish.title": "Publish / handoff",
  "publish.badge": "Plan of record · Oxbo-Z baseline + Aura",
  "publish.note":
    "Publishing turns this version into the plan of record: it becomes the plan-vs-actual baseline Oxbo-Z reads, and it is emitted to Aura in place of the incumbent AFE-Design feed. Review the validation checklist and the diff before continuing — blocking items disable publishing.",
  "publish.ctx.well": "Well",
  "publish.ctx.event": "Event",
  "publish.ctx.afe": "AFE",
  "publish.checklist.title": "Validation checklist",
  "publish.count.ok": "OK",
  "publish.count.warn": "warning",
  "publish.count.blocking": "blocking",
  "publish.sev.ok": "OK",
  "publish.sev.warn": "Warning",
  "publish.sev.blocking": "Blocking",
  "publish.fix": "Fix ↗",
  "publish.check.requiredFields": "Required fields complete",
  "publish.check.requiredFields.ok":
    "Objective, program, hole plan, casing and cost plan all carry their required fields.",
  "publish.check.tvdLeMd": "TVD ≤ MD on every section",
  "publish.check.tvdLeMd.ok": "Planned TVD does not exceed final MD; geometry is consistent.",
  "publish.check.monotonic": "Monotonic depth",
  "publish.check.monotonic.ok": "The program phases advance without overlap or retreat in depth.",
  "publish.check.costBalance": "Cost lines balanced",
  "publish.check.costBalance.ok": "The cost plan sums within tolerance of the authorized target.",
  "publish.check.casingNesting": "Casing nesting valid",
  "publish.check.casingNesting.ok": "Every hole section nests inside a casing string; the design is valid.",
  "publish.check.programSeq": "Program sequence complete",
  "publish.check.programSeq.ok": "The program phases are sequenced 1..N with no gaps.",
  "publish.diff.title": "Diff vs. previously published version",
  "publish.diff.sub": "Comparison v{prior} published → v{next} (to publish) — what Oxbo-Z and Aura receive if you continue.",
  "publish.diff.first": "No prior published version — this is the first publish for this event.",
  "publish.diff.none": "No differences to show against the last published version.",
  "publish.rail.title": "Publish plan",
  "publish.rail.hint":
    "On publish, this version replaces the current baseline in Oxbo-Z and is emitted to Aura. The action is recorded in the version history (W8).",
  "publish.action": "Publish plan",
  "publish.publishing": "Publishing…",
  "publish.published": "Published",
  "publish.error": "Publish failed — try again.",
  "publish.gate.blocked": "{n} blocking item(s) — fix them before publishing.",
  "publish.gate.notReady": "Validation is not ready yet.",
  "publish.gate.notApproved": "Plan is {status} — only an approved or locked plan can publish.",
  "publish.gate.ready": "All checks clear — ready to publish.",
  "publish.done": "Plan published · v{version}.",
  "publish.done.z": "Baseline sent to Oxbo-Z.",
  "publish.done.aura": "secuencia_operativa emitted to Aura ({n} data types).",
  "publish.targets.title": "Publish targets",
  "publish.target.z": "Oxbo-Z (baseline)",
  "publish.target.z.sub": "Plan-vs-actual baseline for reporting.",
  "publish.target.aura": "Aura (secuencia_operativa)",
  "publish.target.aura.sub": "AFE-Design ingest — replaces the incumbent feed.",
  "versions.title": "Versions & supplements",
  "versions.badge": "Authoring · Drilling engineer / Planner",
  "versions.empty": "No plan yet on this well — start one from the Plan finder.",
  "versions.noVersions": "This event has no plan versions yet.",
  "versions.ctx.well": "Well",
  "versions.ctx.event": "Event",
  "versions.ctx.afe": "AFE",
  "versions.note":
    "The plan is append-only: each published edit creates a new version (v1, v2, v3…); prior versions become read-only. AFE supplements are new authorizations tied to the original plan — they don't replace the version, they adjust the authorized total.",
  "versions.kpi.original": "Original AFE",
  "versions.kpi.approved": "Approved supplements ({n})",
  "versions.kpi.pending": "In-flight supplements ({n})",
  "versions.kpi.authorized": "Authorized total",
  "versions.rollup.original": "original",
  "versions.rollup.approved": "{n} approved",
  "versions.rollup.authorized": "authorized total in force",
  "versions.history.title": "Version history",
  "versions.history.count": "{n} version(s)",
  "versions.history.publishHint": "A version can be published to Oxbo-Z + Aura only once it reaches approved / locked.",
  "versions.publish.ready": "Publishable",
  "versions.publish.notReady": "Not yet publishable",
  "versions.item.published": "Published to Oxbo-Z + Aura",
  "versions.diff.title": "Change summary",
  "versions.diff.sub": "What changed between the selected version and its prior version.",
  "versions.diff.chip": "v{a} vs v{b}",
  "versions.diff.initial": "v{a} · initial version (no prior to compare)",
  "versions.diff.field": "Field",
  "versions.diff.before": "Before",
  "versions.diff.after": "After",
  "versions.diff.programa": "Operative program",
  "versions.diff.costo": "Cost plan",
  "versions.diff.targets": "Targets",
  "versions.diff.phases": "Phases (program)",
  "versions.diff.days": "Target duration (days)",
  "versions.diff.afeTotal": "AFE line total",
  "versions.diff.note":
    "This diff compares design datasets (program / cost / targets) between published versions. It excludes unpublished intermediate drafts.",
  "versions.supp.title": "AFE supplements",
  "versions.supp.request": "Request supplement",
  "versions.supp.lockedHint": "A locked plan cannot take new supplements.",
  "versions.supp.lockedMsg":
    "This plan is locked / published — new supplements are not accepted. Create a new version to change the authorization.",
  "versions.supp.empty": "No supplements on this plan yet.",
  "versions.supp.col.seq": "Supp",
  "versions.supp.col.reason": "Reason",
  "versions.supp.col.amount": "Amount (+US$)",
  "versions.supp.col.by": "Requested by",
  "versions.supp.col.status": "Status",
  "versions.supp.col.date": "Date",
  "versions.supp.state.requested": "Under review",
  "versions.supp.state.approved": "Approved",
  "versions.supp.state.rejected": "Rejected",
  "versions.supp.sumApproved": "Σ approved supplements",
  "versions.supp.authorizedNote": "authorized in force",
  "versions.supp.formTitle": "Request supplement",
  "versions.supp.formSub": "New authorization tied to AFE {afe}",
  "versions.supp.f.reason": "Reason",
  "versions.supp.f.reasonPh":
    "Describe the cause of the supplement (plan deviation, unforeseen condition, scope change…)",
  "versions.supp.f.amount": "Requested amount",
  "versions.supp.f.projected": "Projected authorized total",
  "versions.supp.f.hint": "The approval process is configurable per organization (D18/D24).",
  "versions.supp.f.send": "Submit supplement",
  "versions.supp.f.sending": "Submitting…",
  "versions.trail.title": "Approval trail",
  "versions.trail.empty": "No supplement is in an approval trail right now.",
  "versions.trail.chip": "Supp #{seq} · in progress",
  "versions.trail.sub":
    "Configurable multi-step process — Field → Office → JV. Each step stays locked until the previous one approves.",
  "versions.trail.stage.field": "Field",
  "versions.trail.stage.office": "Office",
  "versions.trail.stage.jv": "JV",
  "versions.trail.state.approved": "Approved",
  "versions.trail.state.pending": "Under review",
  "versions.trail.state.locked": "Locked",
  "versions.trail.state.rejected": "Rejected",
  "versions.trail.approve": "Approve",
  "versions.trail.reject": "Reject",
  "placeholder.subtitle": "Screen scaffolded — implementation lands in the W1–W9 fan-out.",
  "placeholder.comingSoon": "coming soon.",
  "common.loading": "Loading…",
  "common.error": "Something went wrong.",
  "common.retry": "Retry",
  "common.na": "—",
  "common.of": "of",
  "common.close": "Close",
  // W7 — plan timeline
  "timeline.title": "Plan timeline — multi-track editor",
  "timeline.badge": "Authoring · Drilling engineer / Planner",
  "timeline.save": "Save timeline",
  "timeline.saving": "Saving…",
  "timeline.note":
    "Authoring editor — sequence plan bars (outline) per track, drag one to re-time it, or click a bar to edit its dates and depth. The shaded band right of Today is the look-ahead horizon. Actual and NPT bars overlay read-only from Oxbo-Z.",
  "timeline.ctx.well": "Well",
  "timeline.ctx.event": "Event",
  "timeline.zoom": "Zoom",
  "timeline.zoom.day": "Day",
  "timeline.zoom.week": "Week",
  "timeline.zoom.month": "Month",
  "timeline.show": "Show",
  "timeline.pa.both": "Plan + Actual",
  "timeline.pa.plan": "Plan only",
  "timeline.pa.actual": "Actual only",
  "timeline.todayLine": "Today line",
  "timeline.opFilter": "Filter timeline by operation type",
  "timeline.operations": "Operations",
  "timeline.op.pad": "Pad construction",
  "timeline.op.drilling": "Drilling",
  "timeline.op.unconv": "Unconventional completion",
  "timeline.op.completion": "Completion",
  "timeline.op.wireline": "Wireline",
  "timeline.op.ct": "Coiled tubing",
  "timeline.op.workover": "Workover",
  "timeline.legend.plan": "Plan (draggable)",
  "timeline.legend.actual": "Actual",
  "timeline.legend.npt": "NPT",
  "timeline.legend.milestone": "Milestone",
  "timeline.legend.lookahead": "Look-ahead horizon",
  "timeline.canvasAria":
    "Multi-track plan Gantt: the critical-path-equipment spine, well tracks, and shared services across the schedule window, with editable dashed plan bars, filled actual bars, an NPT segment, milestone diamonds, a Today line and a shaded look-ahead band.",
  "timeline.empty": "No tracks yet — this plan has no timeline scope defined.",
  "timeline.kind.cpe": "Critical-path equipment",
  "timeline.kind.well": "Well",
  "timeline.kind.service": "Shared service",
  "timeline.kind.pad": "Pad",
  "timeline.grid.title": "Plan bars by track",
  "timeline.grid.hint":
    "Add, edit, reorder (↑ ↓) or delete plan steps here — the timeline above mirrors every change. Actual and NPT overlays are not listed; they are read-only.",
  "timeline.addStep": "Add plan step",
  "timeline.trackEmpty": "No plan steps on this track — add the first.",
  "timeline.selectRow": "Select this step",
  "timeline.labelPlaceholder": "Step label…",
  "timeline.phasePlaceholder": "Phase…",
  "timeline.col.seq": "Seq.",
  "timeline.col.label": "Label",
  "timeline.col.start": "Start",
  "timeline.col.end": "End",
  "timeline.col.phase": "Phase",
  "timeline.editor.title": "Edit plan step",
  "timeline.editor.hint": "Select a plan bar on the timeline or in a grid to edit it here.",
  "timeline.editor.readonly": "This is an actual / NPT overlay from Oxbo-Z — read-only.",
  "timeline.editor.label": "Label",
  "timeline.editor.start": "Start",
  "timeline.editor.end": "End",
  "timeline.editor.depthFrom": "Depth from (m)",
  "timeline.editor.depthUntil": "Depth to (m)",
  "timeline.editor.cpe": "CPE assigned",
  "timeline.editor.phase": "Phase",
  "timeline.editor.delete": "Delete step",
  "timeline.kpi.planBars": "Plan bars",
  "timeline.kpi.tracks": "Tracks",
  "timeline.kpi.lookahead": "Look-ahead",
  "timeline.kpi.weeksUnit": "wk",
  "timeline.footnote":
    "The plan timeline is the pad-scoped baseline Oxbo-Z reads for plan-vs-actual and that publish emits to Aura as secuencia_operativa. Plan bars authored here are the CPE (rig / frac / CT) schedule; actuals overlay them read-only.",
  "common.cancel": "Cancel",
};

const es: Catalog = {
  "app.name": "Oxbo-W",
  "topbar.wellSwitcher": "Cambiar pozo / pad",
  "topbar.theme": "Tema",
  "topbar.theme.dark": "Oscuro",
  "topbar.theme.light": "Claro",
  "topbar.theme.auto": "Auto",
  "topbar.locale": "Idioma",
  "topbar.lineage": "Linaje de datos",
  "topbar.lineage.adminOnly": "solo admin",
  "topbar.lineage.on": "Activado",
  "topbar.lineage.off": "Desactivado",
  "settings.title": "Ajustes",
  "settings.open": "Abrir ajustes",
  "settings.tenant": "Espacio de trabajo",
  "settings.units": "Unidades",
  "settings.units.auto": "Auto (predeterminado)",
  "settings.units.hint": "Convertí cada medición a tu sistema de unidades preferido.",
  "settings.currency": "Moneda",
  "settings.currency.native": "Nativa",
  "nav.collapse": "Contraer menú",
  "nav.expand": "Expandir menú",
  "well.switcher.title": "Pozos",
  "well.switcher.all": "Todos los pozos",
  "nav.finder": "Buscador de planes",
  "nav.workspace": "Plan Workspace",
  "nav.holePlan": "Hole plan",
  "nav.casing": "Entubación / Casing",
  "nav.cost": "Plan de costos / AFE",
  "nav.program": "Programa operativo",
  "nav.trajectory": "Trayectoria",
  "nav.timeline": "Línea de tiempo del plan",
  "nav.versions": "Versiones y suplementos",
  "nav.publish": "Publicar / handoff",
  "letter.P": "Perforación",
  "letter.T": "Terminación",
  "letter.L": "Pulling",
  "letter.W": "Workover",
  "letter.S": "Sidetrack",
  "conv.yes": "Conv.",
  "conv.no": "No conv.",
  "conv.convention": "Convencional",
  "conv.unconvention": "No convencional",
  "plan.status.none": "Sin plan",
  "plan.status.draft": "Borrador v{n}",
  "plan.status.review": "En revisión v{n}",
  "plan.status.approved": "Aprobado v{n}",
  "plan.status.locked": "Bloqueado v{n}",
  "plan.status.published": "Publicado v{n}",
  "finder.title": "Buscador de planes",
  "finder.badge": "Punto de entrada · Oxbo-W",
  "finder.lede":
    "Encuentre un pozo y evento para abrir su plan, o inicie uno nuevo. Un pozo puede tener eventos de perforación, terminación, workover y pulling a la vez — cada uno con su propio plan y versión.",
  "finder.newPlan": "Nuevo plan",
  "finder.filter.basin": "Cuenca",
  "finder.filter.field": "Yacimiento",
  "finder.filter.pad": "Pad",
  "finder.filter.well": "Pozo / UWI",
  "finder.filter.allBasin": "Toda cuenca",
  "finder.filter.allField": "Todo yacimiento",
  "finder.filter.allPad": "Todo pad",
  "finder.filter.wellPlaceholder": "Buscar por nombre de pozo o UWI…",
  "finder.filter.eventType": "Tipo de evento",
  "finder.clear": "Limpiar",
  "finder.clearAll": "Limpiar todos los filtros",
  "finder.count.wells": "pozos",
  "finder.count.events": "eventos",
  "finder.col.well": "Pozo",
  "finder.col.location": "Cuenca / Yacimiento",
  "finder.col.pad": "Pad",
  "finder.col.events": "Eventos",
  "finder.col.planStatus": "Estado del plan",
  "finder.col.actions": "Acciones",
  "finder.empty": "Sin resultados para los filtros actuales.",
  "finder.action.open": "Abrir",
  "finder.action.newPlan": "Nuevo plan",
  "finder.action.clone": "Clonar plan",
  "finder.action.newVersion": "Nueva versión",
  "finder.source":
    "Datos: registro de pozos/wellbore Oxbo-M + event_codes (letra + convencional) · plan de record Oxbo-P. Letra de evento vía tipo_evento_enrichment — mismo mapa que Aura AFE-Design.",
  "wizard.title": "Nuevo plan",
  "wizard.step.well": "Pozo",
  "wizard.step.eventType": "Tipo de evento",
  "wizard.step.confirm": "Confirmar",
  "wizard.searchWell": "Buscar pozo",
  "wizard.searchWellPlaceholder": "Nombre de pozo, UWI, pad o yacimiento…",
  "wizard.pickWell": "Seleccionar pozo",
  "wizard.noMatch": "Sin coincidencias.",
  "wizard.chosenWell": "Pozo",
  "wizard.eventTypePrompt": "Tipo de evento — seleccione y confirme convencional / no convencional",
  "wizard.note":
    "Se creará un plan en estado Borrador v1. Podrá editar objetivo, programa, hole plan, casing y costos en el Plan Workspace inmediatamente.",
  "wizard.sum.well": "Pozo",
  "wizard.sum.uwi": "UWI",
  "wizard.sum.eventType": "Tipo de evento",
  "wizard.sum.conv": "Convencional / No conv.",
  "wizard.sum.afe": "AFE (asignado al crear)",
  "wizard.sum.afeAuto": "se genera al crear",
  "wizard.back": "Atrás",
  "wizard.cancel": "Cancelar",
  "wizard.next": "Continuar",
  "wizard.create": "Crear plan (Borrador v1)",
  "wizard.creating": "Creando…",
  "cost.title": "Plan de costos · Líneas AFE",
  "cost.lede":
    "Pantalla de autoría — cuadrícula editable WBS (paso de programa) × CBS (cuenta). Los cambios se guardan por línea; publicar el plan lo envía a Oxbo-Z como línea base.",
  "cost.boundary":
    "Oxbo-W autoría el PLAN: presupuesto, método de devengo, unidad, cantidad planificada y tarifa. Comprometido, devengado y real provienen del motor de devengo y son de solo lectura en Oxbo-Z — no se editan aquí.",
  "cost.save": "Guardar cambios",
  "cost.saving": "Guardando…",
  "cost.addLine": "+ Agregar línea AFE",
  "cost.empty": "Aún no hay líneas AFE — use “Agregar línea AFE” para comenzar.",
  "cost.ctx.well": "Pozo",
  "cost.ctx.afe": "AFE",
  "cost.ctx.total": "presupuesto autorizado",
  "cost.rail.title": "Secuencia del programa",
  "cost.rail.hint": "Seleccione un paso para filtrar las líneas AFE de esa etapa.",
  "cost.step.all": "Todos los pasos",
  "cost.step.allMeta": "{n} líneas",
  "cost.step.global": "Global (sin paso)",
  "cost.grid.title": "Líneas AFE",
  "cost.grid.subAll": "— todos los pasos del programa",
  "cost.col.account": "Cuenta (CoA · Oxbo-M)",
  "cost.col.description": "Descripción",
  "cost.col.programStep": "Paso de programa",
  "cost.col.accrual": "Método de devengo",
  "cost.col.unit": "Unidad",
  "cost.col.qty": "Cant.",
  "cost.col.rate": "Tarifa",
  "cost.col.budget": "Presupuesto",
  "cost.account.placeholder": "Cuenta…",
  "cost.unit.placeholder": "un",
  "cost.budget.derived": "Presupuesto = cantidad planificada × tarifa (autoritativo del servidor)",
  "cost.total.all": "Total — todas las líneas",
  "cost.total.step": "Total — este paso",
  "cost.phase.title": "Costo por fase (presupuesto AFE)",
  "cost.accrual.avance_etapas": "Por etapas (avance)",
  "cost.accrual.devengado": "Devengado",
  "cost.accrual.adjusted_cost": "Costo ajustado",
  "cost.accrual.straight_line": "Línea recta",
  "program.title": "Programa operativo",
  "program.badge": "Autoría · Ingeniero de perforación / Planificador",
  "program.save": "Guardar programa",
  "program.saving": "Guardando…",
  "program.note":
    "Este es el programa operativo — la secuencia ordenada de fases que gobierna la curva de plan (profundidad vs. días) leída por Oxbo-Z y el reconciliador. Reordene para cambiar el eje de días; el tiempo acumulado y la curva se recalculan con cada cambio.",
  "program.ctx.well": "Pozo",
  "program.ctx.event": "Evento",
  "program.ctx.afe": "AFE",
  "program.kpi.phases": "Fases en secuencia",
  "program.kpi.totalTime": "Tiempo total (acum.)",
  "program.kpi.dol": "DOL total",
  "program.kpi.finalDepth": "Profundidad final (MD)",
  "program.kpi.amount": "Monto total",
  "program.grid.title": "Secuencia de fases",
  "program.grid.hint": "1..N, reordene con ↑ / ↓",
  "program.addPhase": "Agregar fase",
  "program.empty": "Aún no hay fases — agregue la primera fase para construir la curva de plan.",
  "program.total": "Total",
  "program.accHint":
    "Tiempo acum. (h) es la suma corrida de Tiempo (h) en el orden de Secuencia — el eje X de la curva de plan (÷24 = días transcurridos). Editar Tiempo, DOL o Profundidad hasta actualiza la curva de abajo.",
  "program.phasePlaceholder": "Fase…",
  "program.descPlaceholder": "Descripción de actividad…",
  "program.col.seq": "Sec.",
  "program.col.phase": "Fase",
  "program.col.description": "Descripción / código de actividad",
  "program.col.depthFrom": "Prof. desde (m)",
  "program.col.depthUntil": "Prof. hasta (m)",
  "program.col.time": "Tiempo (h)",
  "program.col.dol": "DOL (d)",
  "program.col.pct": "% avance acum.",
  "program.col.amount": "Monto (US$)",
  "program.col.accTime": "Tiempo acum. (h)",
  "program.curve.title": "Curva de plan — profundidad vs. días",
  "program.curve.sub": "Deriva de Secuencia × Tiempo acum. (h)/24 × Profundidad hasta",
  "program.curve.xLabel": "Días acumulados (Tiempo acum. h ÷ 24)",
  "program.curve.yLabel": "Profundidad MD (m)",
  "program.curve.aria":
    "Curva de plan mostrando profundidad medida acumulada contra días transcurridos, un vértice por fase en orden de secuencia.",
  "program.curve.legend": "Curva de plan",
  "program.footnote":
    "El programa operativo se adapta al tipo de evento (perforación / terminación / workover). Para eventos de terminación el eje de profundidad puede ser plano o decreciente en algunas fases (punzados, flowback) — la curva sigue siendo monótona en el eje de días, no necesariamente en profundidad neta cuando la fase no perfora.",
  // W6 — trayectoria
  "traj.title": "Plan de trayectoria",
  "traj.badge": "Autoría · Ingeniero direccional / Planificador",
  "traj.save": "Guardar + calcular",
  "traj.computing": "Calculando…",
  "traj.note":
    "Estaciones de desvío planificadas — MD · inclinación · azimut son editables; TVD · N/S · E/W · DLS se calculan por mínima curvatura en el servidor (solo lectura, provenance = computed). Al editar se limpian las columnas calculadas hasta el próximo guardado.",
  "traj.computeHint":
    "Método: mínima curvatura entre estaciones consecutivas. El servidor devuelve TVD / N-S / E-W / DLS; las dos vistas y los KPI se redibujan con lo que devuelve.",
  "traj.computed": "calculado",
  "traj.kop": "KOP",
  "traj.ctx.well": "Pozo",
  "traj.ctx.event": "Evento",
  "traj.ctx.afe": "AFE",
  "traj.kpi.stations": "Estaciones de desvío",
  "traj.kpi.finalMd": "MD final (planificada)",
  "traj.kpi.targetTvd": "TVD objetivo",
  "traj.kpi.kop": "KOP (inicio de curva)",
  "traj.kpi.displacement": "Desplazamiento lateral (horiz.)",
  "traj.kpi.maxDls": "DLS máx. planificado",
  "traj.grid.title": "Estaciones de desvío planificadas",
  "traj.grid.hint": "vertical → curva → lateral, ordenadas por MD",
  "traj.addStation": "Agregar estación de desvío",
  "traj.empty":
    "Aún no hay estaciones — agregá la estación de superficie (MD 0) para empezar el plan.",
  "traj.col.segment": "Segmento",
  "traj.col.md": "MD (m)",
  "traj.col.inc": "Inclinación (°)",
  "traj.col.azi": "Azimut (°)",
  "traj.col.tvd": "TVD (m)",
  "traj.col.ns": "N/S (m)",
  "traj.col.ew": "E/W (m)",
  "traj.col.dls": "DLS (°/30m)",
  "traj.seg.vertical": "Vertical",
  "traj.seg.build": "Curva (build)",
  "traj.seg.lateral": "Lateral",
  "traj.section.title": "Sección vertical",
  "traj.section.sub": "TVD vs. desplazamiento horizontal",
  "traj.section.xLabel": "Desplazamiento horizontal (m)",
  "traj.section.yLabel": "TVD (m)",
  "traj.section.aria":
    "Sección vertical del pozo — profundidad vertical verdadera contra desplazamiento horizontal, coloreada por segmento (vertical, curva, lateral).",
  "traj.section.empty":
    "Aún no hay TVD calculado — guardá las estaciones para calcular la sección.",
  "traj.plan.title": "Vista en planta — N/S vs. E/W",
  "traj.plan.sub": "vista superior, norte arriba",
  "traj.plan.xLabel": "E/W (m)",
  "traj.plan.yLabel": "N/S (m)",
  "traj.plan.aria":
    "Vista en planta del pozo — coordenada norte contra este desde la boca de pozo en el origen, coloreada por segmento.",
  "traj.plan.empty":
    "Aún no hay N/E calculado — guardá las estaciones para calcular la vista en planta.",
  "traj.na.title": "La trayectoria no aplica a este evento",
  "traj.na.body":
    "La trayectoria de desvío se planifica para eventos que perforan un pozo (perforación / sidetrack). Los eventos livianos (pulling / workover / coiled tubing) no llevan plan de trayectoria.",
  "traj.footnote":
    "La trayectoria planificada es la línea base que Oxbo-Z lee para real-vs-plan (las pasadas MWD/Gyro reales se superponen contra ella al comenzar la perforación). El cálculo de mínima curvatura es autoritativo en el servidor, de modo que el plan de W y los reales de Z coinciden al número.",
  "holePlan.title": "Diseñador de hole plan",
  "holePlan.badge": "Autoría · Ingeniero de perforación / Planificador",
  "holePlan.copyPrior": "Copiar de plan anterior",
  "holePlan.save": "Guardar hole plan",
  "holePlan.saving": "Guardando…",
  "holePlan.savedAt": "Guardado {time}",
  "holePlan.note":
    "El hole plan define las secciones perforadas por diámetro e intervalo MD; alimenta el diseño de casing (W3), el programa operativo (W5) y la curva de plan. Los cambios quedan en la versión borrador hasta Publicar (W9).",
  "holePlan.gridTitle": "Secciones del hole plan",
  "holePlan.gridHint":
    "Reordená las filas con los controles ↑ ↓. Hacé clic en una sección para resaltarla en el esquema. El diámetro puede tomarse del catálogo de brocas / hole-size de Oxbo-M.",
  "holePlan.footHint":
    "El Base MD de cada sección debe igualar el Top MD de la siguiente. El largo se calcula automáticamente (Base − Top).",
  "holePlan.addSection": "+ Agregar sección",
  "holePlan.empty": "Aún no hay secciones — agregá la primera sección perforada.",
  "holePlan.noPlan": "No hay plan seleccionado. Abrí un plan desde el buscador primero.",
  "holePlan.unnamed": "Sección sin nombre",
  "holePlan.selectHint": "Resaltar esta sección en el esquema",
  "holePlan.selectFirst": "Seleccioná primero una fila de sección",
  "holePlan.diaCatalog": "Catálogo de diámetro (Oxbo-M)",
  "holePlan.schematic": "Esquema del pozo",
  "holePlan.schematicAria":
    "Esquema vertical del pozo que muestra las secciones telescópicas desde el conductor más ancho en superficie hasta la sección más angosta en profundidad final, con las zapatas de casing en la base de cada sección.",
  "holePlan.kpi.sections": "Secciones",
  "holePlan.kpi.td": "MD total (TD)",
  "holePlan.kpi.dol": "DOL acumulado",
  "holePlan.kpi.cost": "Costo objetivo total",
  "holePlan.col.phase": "Fase",
  "holePlan.col.section": "Sección",
  "holePlan.col.diameter": "Diámetro (in)",
  "holePlan.col.topMd": "Top MD (m)",
  "holePlan.col.baseMd": "Base MD (m)",
  "holePlan.col.length": "Largo (m)",
  "holePlan.col.dol": "DOL (d)",
  "holePlan.col.targetDuration": "Duración obj. (d)",
  "holePlan.col.targetCost": "Costo objetivo (US$)",
  "holePlan.ctx.well": "Pozo",
  "holePlan.ctx.event": "Evento",
  "holePlan.ctx.afe": "AFE",
  "holePlan.na.title": "El hole plan no aplica a este evento",
  "holePlan.na.body":
    "El evento seleccionado ({code}) reutiliza el pozo perforado existente y no requiere diseño de sección de hoyo. Este plan usa el programa operativo (W5) y el plan de costos (W4) en su lugar.",
  "holePlan.na.toProgram": "Ir a programa operativo",
  "holePlan.na.toCost": "Ir a plan de costos",
  "casing.title": "Entubación · Diseño tubular",
  "casing.badge": "Autoría · Ingeniero de perforación / Planificador",
  "casing.importHole": "Importar desde hole plan",
  "casing.save": "Guardar entubación",
  "casing.saving": "Guardando…",
  "casing.savedAt": "Guardado {time}",
  "casing.note":
    "El diseño de entubación coloca una sarta tubular en cada sección perforada — desde el Conductor más externo en superficie hasta la sarta de Producción más interna en TD. El OD de casing se vincula al catálogo de tubulares de Oxbo-M; el anidamiento se valida al editar. Los cambios quedan en la versión borrador hasta Publicar (W9).",
  "casing.gridTitle": "Sartas de entubación",
  "casing.gridHint":
    "El orden va Conductor → Superficie → Intermedia → Producción (de externa a interna). Reordená las filas con los controles ↑ ↓. Hacé clic en una sarta para resaltarla en el esquema. El OD de casing se toma del catálogo de tubulares de Oxbo-M.",
  "casing.addString": "+ Agregar sarta",
  "casing.empty": "Aún no hay sartas de entubación — agregá la primera sarta (usualmente el Conductor).",
  "casing.noPlan": "No hay plan seleccionado. Abrí un plan desde el buscador primero.",
  "casing.unnamed": "Sarta sin nombre",
  "casing.selectHint": "Resaltar esta sarta en el esquema",
  "casing.schematic": "Esquema de entubación",
  "casing.schematicAria":
    "Esquema concéntrico de entubación que muestra las sartas tubulares anidadas desde el conductor más ancho en superficie hasta la sarta de producción más angosta en profundidad final, con una zapata de casing en la base de cada sarta.",
  "casing.ok": "OK",
  "casing.od.placeholder": "Tubular…",
  "casing.type.choose": "Tipo…",
  "casing.type.conductor": "Conductor",
  "casing.type.surface": "Superficie",
  "casing.type.intermediate": "Intermedia",
  "casing.type.production": "Producción",
  "casing.kpi.strings": "Sartas diseñadas",
  "casing.kpi.deepestShoe": "Zapata más profunda",
  "casing.kpi.productionOd": "Casing de producción",
  "casing.kpi.warnings": "Alertas de anidamiento",
  "casing.col.string": "Sarta",
  "casing.col.type": "Tipo",
  "casing.col.section": "Sección",
  "casing.col.hole": "Hoyo (in)",
  "casing.col.od": "Casing OD (in)",
  "casing.col.top": "Tope (m)",
  "casing.col.base": "Base (m)",
  "casing.col.length": "Largo (m)",
  "casing.col.nesting": "Anidamiento",
  "casing.warn.od_ge_hole": "OD ≥ hoyo",
  "casing.warn.od_ge_prev": "OD ≥ sarta previa",
  "casing.warn.base_le_prev": "Base ≤ sarta previa",
  "casing.nest.warnTitle": "Anidamiento inconsistente en {name}:",
  "casing.nest.warnBody":
    "el OD de casing seleccionado no cabe dentro del hoyo de su propia sección, o no anida dentro de la sarta anterior. Ajustá el tubular o el diámetro de hoyo antes de publicar.",
  "casing.nest.rule":
    "Regla de anidamiento: para cada sarta, Casing OD < Hoyo de su sección < Casing OD de la sarta anterior, y cada sarta llega más profundo que la de arriba. Conductor (más externa) → Producción (más interna).",
  "casing.ctx.well": "Pozo",
  "casing.ctx.event": "Evento",
  "casing.ctx.afe": "AFE",
  "casing.na.title": "El diseño de entubación no aplica a este evento",
  "casing.na.body":
    "El evento seleccionado ({code}) no corre casing y no requiere diseño tubular. Este plan usa el programa operativo (W5) y el plan de costos (W4) en su lugar.",
  "casing.na.toProgram": "Ir a programa operativo",
  "casing.na.toCost": "Ir a plan de costos",
  "ws.title": "Plan Workspace",
  "ws.badge": "Autoría · Ingeniero de perforación / Planificador",
  "ws.publish": "Publicar",
  "ws.empty":
    "Ningún evento seleccionado. Abra un pozo y evento desde el Buscador de planes para editar su plan.",
  "ws.ctx.well": "Pozo",
  "ws.ctx.event": "Evento",
  "ws.ctx.afe": "AFE",
  "ws.note":
    "Este workspace es autoría. Objetivo y atributos se editan aquí; el programa, hole plan, casing, costos y trayectoria abren en sus propias pantallas. Los cambios quedan en la versión borrador hasta Publicar, que envía la línea base a Oxbo-Z y emite secuencia_operativa a Aura.",
  "ws.kpi.duration": "Duración objetivo",
  "ws.kpi.cost": "Costo objetivo",
  "ws.kpi.finalMd": "Profundidad final (MD)",
  "ws.kpi.rig": "Equipo planificado",
  "ws.tabs.aria": "Datasets de diseño del plan",
  "ws.tab.objetivo": "Objetivo",
  "ws.tab.programa": "Programa",
  "ws.tab.holePlan": "Hole plan",
  "ws.tab.casing": "Casing",
  "ws.tab.cost": "Plan de costos",
  "ws.tab.atributos": "Atributos",
  "ws.tab.fractura": "Fractura / Etapas",
  "ws.objetivo.title": "Objetivo planificado",
  "ws.objetivo.empty": "Aún no hay atributos de objetivo.",
  "ws.atributos.title": "Atributos",
  "ws.atributos.empty": "Aún no hay atributos.",
  "ws.switcher.aria": "Cambiar evento / plan",
  "ws.switcher.heading": "{well} · eventos de este pozo",
  "ws.curve.title": "Curva de plan — profundidad vs. días",
  "ws.curve.sub": "MD (m) acumulado por fase",
  "ws.curve.aria":
    "Curva de plan que muestra la profundidad medida acumulada contra los días transcurridos, un marcador por fase del programa.",
  "ws.curve.xAxis": "Días transcurridos (DOL acumulado)",
  "ws.curve.yAxis": "MD (m)",
  "ws.curve.series": "Plan de profundidad (versión actual)",
  "ws.version.title": "Versión actual",
  "ws.version.editedBy": "Editado por {actor}",
  "ws.version.count": "{n} versión(es) en registro",
  "ws.version.none": "Aún no hay plan en este evento — inicie uno desde el Buscador de planes.",
  "ws.version.history": "Historial de versiones",
  "ws.publishCard.title": "Publicar plan de registro",
  "ws.publishCard.body":
    "Publicar envía esta versión como línea base a Oxbo-Z (plan-vs-real) y emite el payload secuencia_operativa a Aura (AFE / programa / costos / hole plan / casing / atributos).",
  "ws.publishCard.action": "Publicar a Oxbo-Z + Aura",
  "publish.title": "Publicar / handoff",
  "publish.badge": "Plan de registro · línea base Oxbo-Z + Aura",
  "publish.note":
    "Publicar convierte esta versión en el plan de registro: se vuelve la línea base plan-vs-real que lee Oxbo-Z, y se emite a Aura en reemplazo del sistema incumbente AFE-Design. Revise el checklist de validación y el diff antes de continuar — los ítems bloqueantes deshabilitan la publicación.",
  "publish.ctx.well": "Pozo",
  "publish.ctx.event": "Evento",
  "publish.ctx.afe": "AFE",
  "publish.checklist.title": "Checklist de validación",
  "publish.count.ok": "OK",
  "publish.count.warn": "advertencia",
  "publish.count.blocking": "bloqueante",
  "publish.sev.ok": "OK",
  "publish.sev.warn": "Advertencia",
  "publish.sev.blocking": "Bloqueante",
  "publish.fix": "Corregir ↗",
  "publish.check.requiredFields": "Campos obligatorios completos",
  "publish.check.requiredFields.ok":
    "Objetivo, programa, hole plan, casing y plan de costos tienen todos sus campos requeridos.",
  "publish.check.tvdLeMd": "TVD ≤ MD en todos los tramos",
  "publish.check.tvdLeMd.ok": "La TVD planificada no supera la MD final; la geometría es consistente.",
  "publish.check.monotonic": "Profundidad monotónica",
  "publish.check.monotonic.ok": "Las fases del programa avanzan sin superposición ni retroceso en profundidad.",
  "publish.check.costBalance": "Líneas de costo balanceadas",
  "publish.check.costBalance.ok": "El plan de costos suma dentro de la tolerancia del objetivo autorizado.",
  "publish.check.casingNesting": "Anidamiento de casing válido",
  "publish.check.casingNesting.ok": "Cada sección del hoyo anida dentro de un string de casing; el diseño es válido.",
  "publish.check.programSeq": "Secuencia de programa completa",
  "publish.check.programSeq.ok": "Las fases del programa están secuenciadas 1..N sin huecos.",
  "publish.diff.title": "Diff vs. versión previamente publicada",
  "publish.diff.sub": "Comparación v{prior} publicada → v{next} (a publicar) — esto recibirán Oxbo-Z y Aura si continúa.",
  "publish.diff.first": "No hay versión previa publicada — esta es la primera publicación de este evento.",
  "publish.diff.none": "No hay diferencias que mostrar contra la última versión publicada.",
  "publish.rail.title": "Publicar plan",
  "publish.rail.hint":
    "Al publicar, esta versión reemplaza la línea base actual en Oxbo-Z y se emite a Aura. La acción queda registrada en el historial de versiones (W8).",
  "publish.action": "Publicar plan",
  "publish.publishing": "Publicando…",
  "publish.published": "Publicado",
  "publish.error": "Falló la publicación — reintente.",
  "publish.gate.blocked": "{n} ítem(es) bloqueante(s) — corríjalos antes de publicar.",
  "publish.gate.notReady": "La validación aún no está lista.",
  "publish.gate.notApproved": "El plan está {status} — solo un plan aprobado o bloqueado puede publicarse.",
  "publish.gate.ready": "Todos los checks resueltos — listo para publicar.",
  "publish.done": "Plan publicado · v{version}.",
  "publish.done.z": "Línea base enviada a Oxbo-Z.",
  "publish.done.aura": "secuencia_operativa emitida a Aura ({n} tipos de datos).",
  "publish.targets.title": "Destinos de publicación",
  "publish.target.z": "Oxbo-Z (línea base)",
  "publish.target.z.sub": "Línea base plan-vs-real para reporte.",
  "publish.target.aura": "Aura (secuencia_operativa)",
  "publish.target.aura.sub": "Ingesta AFE-Design — reemplaza el sistema incumbente.",
  "versions.title": "Versiones y suplementos",
  "versions.badge": "Autoría · Ingeniero de perforación / Planificador",
  "versions.empty": "Aún no hay plan en este pozo — iniciá uno desde el Buscador de planes.",
  "versions.noVersions": "Este evento aún no tiene versiones de plan.",
  "versions.ctx.well": "Pozo",
  "versions.ctx.event": "Evento",
  "versions.ctx.afe": "AFE",
  "versions.note":
    "El plan es append-only: cada edición publicada crea una nueva versión (v1, v2, v3…); las versiones previas quedan de solo lectura. Los suplementos AFE son autorizaciones nuevas ligadas al plan original — no reemplazan la versión, ajustan el autorizado total.",
  "versions.kpi.original": "AFE original",
  "versions.kpi.approved": "Suplementos aprobados ({n})",
  "versions.kpi.pending": "Suplementos en trámite ({n})",
  "versions.kpi.authorized": "Autorizado total",
  "versions.rollup.original": "original",
  "versions.rollup.approved": "{n} aprobado(s)",
  "versions.rollup.authorized": "autorizado total vigente",
  "versions.history.title": "Historial de versiones",
  "versions.history.count": "{n} versión(es)",
  "versions.history.publishHint": "Una versión se publica a Oxbo-Z + Aura solo cuando llega a aprobado / bloqueado.",
  "versions.publish.ready": "Publicable",
  "versions.publish.notReady": "Aún no publicable",
  "versions.item.published": "Publicado a Oxbo-Z + Aura",
  "versions.diff.title": "Resumen de cambios",
  "versions.diff.sub": "Qué cambió entre la versión seleccionada y su versión previa.",
  "versions.diff.chip": "v{a} vs v{b}",
  "versions.diff.initial": "v{a} · versión inicial (sin comparación previa)",
  "versions.diff.field": "Campo",
  "versions.diff.before": "Antes",
  "versions.diff.after": "Ahora",
  "versions.diff.programa": "Programa operativo",
  "versions.diff.costo": "Plan de costos",
  "versions.diff.targets": "Targets",
  "versions.diff.phases": "Fases (programa)",
  "versions.diff.days": "Duración objetivo (días)",
  "versions.diff.afeTotal": "Total líneas AFE",
  "versions.diff.note":
    "Este diff compara datasets de diseño (programa / costo / targets) entre versiones publicadas. No incluye borradores intermedios no publicados.",
  "versions.supp.title": "Suplementos AFE",
  "versions.supp.request": "Solicitar suplemento",
  "versions.supp.lockedHint": "Un plan bloqueado no admite nuevos suplementos.",
  "versions.supp.lockedMsg":
    "Este plan está bloqueado / publicado — no se aceptan nuevos suplementos. Creá una nueva versión para cambiar la autorización.",
  "versions.supp.empty": "Este plan aún no tiene suplementos.",
  "versions.supp.col.seq": "Supp",
  "versions.supp.col.reason": "Motivo",
  "versions.supp.col.amount": "Monto (+US$)",
  "versions.supp.col.by": "Solicitado por",
  "versions.supp.col.status": "Estado",
  "versions.supp.col.date": "Fecha",
  "versions.supp.state.requested": "En revisión",
  "versions.supp.state.approved": "Aprobado",
  "versions.supp.state.rejected": "Rechazado",
  "versions.supp.sumApproved": "Σ Suplementos aprobados",
  "versions.supp.authorizedNote": "autorizado vigente",
  "versions.supp.formTitle": "Solicitar suplemento",
  "versions.supp.formSub": "Nueva autorización ligada a AFE {afe}",
  "versions.supp.f.reason": "Motivo",
  "versions.supp.f.reasonPh":
    "Describa la causa del suplemento (desvío de plan, condición imprevista, cambio de alcance…)",
  "versions.supp.f.amount": "Monto solicitado",
  "versions.supp.f.projected": "Autorizado total proyectado",
  "versions.supp.f.hint": "El proceso de aprobación es configurable por organización (D18/D24).",
  "versions.supp.f.send": "Enviar suplemento",
  "versions.supp.f.sending": "Enviando…",
  "versions.trail.title": "Trámite de aprobación",
  "versions.trail.empty": "No hay ningún suplemento en trámite en este momento.",
  "versions.trail.chip": "Supp #{seq} · en curso",
  "versions.trail.sub":
    "Proceso multi-paso configurable — Campo → Oficina → JV. Cada paso queda bloqueado hasta que el anterior aprueba.",
  "versions.trail.stage.field": "Campo",
  "versions.trail.stage.office": "Oficina",
  "versions.trail.stage.jv": "JV",
  "versions.trail.state.approved": "Aprobado",
  "versions.trail.state.pending": "En revisión",
  "versions.trail.state.locked": "Bloqueado",
  "versions.trail.state.rejected": "Rechazado",
  "versions.trail.approve": "Aprobar",
  "versions.trail.reject": "Rechazar",
  "placeholder.subtitle": "Pantalla andamiada — la implementación llega en el fan-out W1–W9.",
  "placeholder.comingSoon": "próximamente.",
  "common.loading": "Cargando…",
  "common.error": "Algo salió mal.",
  "common.retry": "Reintentar",
  "common.na": "—",
  "common.of": "de",
  "common.close": "Cerrar",
  // W7 — cronograma del plan
  "timeline.title": "Cronograma del plan — editor multi-pista",
  "timeline.badge": "Autoría · Ingeniero de perforación / Planificador",
  "timeline.save": "Guardar cronograma",
  "timeline.saving": "Guardando…",
  "timeline.note":
    "Editor de autoría — secuencie las barras de plan (contorno) por pista, arrastre una para resecuenciarla, o haga clic en una barra para editar sus fechas y profundidad. La franja sombreada a la derecha de “Hoy” es el horizonte de anticipación. Las barras reales y NPT se superponen en solo lectura desde Oxbo-Z.",
  "timeline.ctx.well": "Pozo",
  "timeline.ctx.event": "Evento",
  "timeline.zoom": "Zoom",
  "timeline.zoom.day": "Día",
  "timeline.zoom.week": "Semana",
  "timeline.zoom.month": "Mes",
  "timeline.show": "Mostrar",
  "timeline.pa.both": "Plan + Real",
  "timeline.pa.plan": "Solo plan",
  "timeline.pa.actual": "Solo real",
  "timeline.todayLine": "Línea “Hoy”",
  "timeline.opFilter": "Filtrar cronograma por tipo de operación",
  "timeline.operations": "Operaciones",
  "timeline.op.pad": "Construcción de pad",
  "timeline.op.drilling": "Perforación",
  "timeline.op.unconv": "Terminación no convencional",
  "timeline.op.completion": "Terminación",
  "timeline.op.wireline": "Wireline",
  "timeline.op.ct": "Coiled tubing",
  "timeline.op.workover": "Workover",
  "timeline.legend.plan": "Plan (arrastrable)",
  "timeline.legend.actual": "Real",
  "timeline.legend.npt": "NPT",
  "timeline.legend.milestone": "Hito",
  "timeline.legend.lookahead": "Horizonte look-ahead",
  "timeline.canvasAria":
    "Cronograma Gantt multi-pista del plan: la pista de equipo de ruta crítica, las pistas de pozo y los servicios compartidos a lo largo de la ventana, con barras de plan punteadas editables, barras reales rellenas, un segmento NPT, rombos de hitos, una línea Hoy y una franja look-ahead sombreada.",
  "timeline.empty": "Aún no hay pistas — este plan no tiene alcance de cronograma definido.",
  "timeline.kind.cpe": "Equipo de ruta crítica",
  "timeline.kind.well": "Pozo",
  "timeline.kind.service": "Servicio compartido",
  "timeline.kind.pad": "Pad",
  "timeline.grid.title": "Barras de plan por pista",
  "timeline.grid.hint":
    "Agregue, edite, reordene (↑ ↓) o elimine pasos de plan aquí — el cronograma de arriba refleja cada cambio. Las superposiciones reales y NPT no se listan; son de solo lectura.",
  "timeline.addStep": "Agregar paso de plan",
  "timeline.trackEmpty": "Sin pasos de plan en esta pista — agregue el primero.",
  "timeline.selectRow": "Seleccionar este paso",
  "timeline.labelPlaceholder": "Etiqueta del paso…",
  "timeline.phasePlaceholder": "Fase…",
  "timeline.col.seq": "Sec.",
  "timeline.col.label": "Etiqueta",
  "timeline.col.start": "Inicio",
  "timeline.col.end": "Fin",
  "timeline.col.phase": "Fase",
  "timeline.editor.title": "Editar paso de plan",
  "timeline.editor.hint": "Seleccione una barra de plan en el cronograma o en una grilla para editarla aquí.",
  "timeline.editor.readonly": "Es una superposición real / NPT de Oxbo-Z — solo lectura.",
  "timeline.editor.label": "Etiqueta",
  "timeline.editor.start": "Inicio",
  "timeline.editor.end": "Fin",
  "timeline.editor.depthFrom": "Prof. desde (m)",
  "timeline.editor.depthUntil": "Prof. hasta (m)",
  "timeline.editor.cpe": "CPE asignado",
  "timeline.editor.phase": "Fase",
  "timeline.editor.delete": "Eliminar paso",
  "timeline.kpi.planBars": "Barras de plan",
  "timeline.kpi.tracks": "Pistas",
  "timeline.kpi.lookahead": "Look-ahead",
  "timeline.kpi.weeksUnit": "sem",
  "timeline.footnote":
    "El cronograma del plan es la línea base a nivel de pad que Oxbo-Z lee para plan-vs-real y que la publicación emite a Aura como secuencia_operativa. Las barras de plan autoradas aquí son el cronograma del CPE (equipo / set de fractura / CT); los reales las superponen en solo lectura.",
  "common.cancel": "Cancelar",
};

const ptBR: Catalog = {
  "app.name": "Oxbo-W",
  "topbar.wellSwitcher": "Trocar poço / pad",
  "topbar.theme": "Tema",
  "topbar.theme.dark": "Escuro",
  "topbar.theme.light": "Claro",
  "topbar.theme.auto": "Auto",
  "topbar.locale": "Idioma",
  "topbar.lineage": "Linhagem de dados",
  "topbar.lineage.adminOnly": "somente admin",
  "topbar.lineage.on": "Ligado",
  "topbar.lineage.off": "Desligado",
  "settings.title": "Configurações",
  "settings.open": "Abrir configurações",
  "settings.tenant": "Espaço de trabalho",
  "settings.units": "Unidades",
  "settings.units.auto": "Auto (padrão)",
  "settings.units.hint": "Converta cada medição para o seu sistema de unidades preferido.",
  "settings.currency": "Moeda",
  "settings.currency.native": "Nativa",
  "nav.collapse": "Recolher menu",
  "nav.expand": "Expandir menu",
  "well.switcher.title": "Poços",
  "well.switcher.all": "Todos os poços",
  "nav.finder": "Buscador de planos",
  "nav.workspace": "Plan Workspace",
  "nav.holePlan": "Hole plan",
  "nav.casing": "Revestimento / Casing",
  "nav.cost": "Plano de custos / AFE",
  "nav.program": "Programa operativo",
  "nav.trajectory": "Trajetória",
  "nav.timeline": "Linha do tempo do plano",
  "nav.versions": "Versões e suplementos",
  "nav.publish": "Publicar / handoff",
  "letter.P": "Perfuração",
  "letter.T": "Completação",
  "letter.L": "Pulling",
  "letter.W": "Workover",
  "letter.S": "Sidetrack",
  "conv.yes": "Conv.",
  "conv.no": "Não conv.",
  "conv.convention": "Convencional",
  "conv.unconvention": "Não convencional",
  "plan.status.none": "Sem plano",
  "plan.status.draft": "Rascunho v{n}",
  "plan.status.review": "Em revisão v{n}",
  "plan.status.approved": "Aprovado v{n}",
  "plan.status.locked": "Bloqueado v{n}",
  "plan.status.published": "Publicado v{n}",
  "finder.title": "Buscador de planos",
  "finder.badge": "Ponto de entrada · Oxbo-W",
  "finder.lede":
    "Encontre um poço e evento para abrir seu plano, ou inicie um novo. Um poço pode ter eventos de perfuração, completação, workover e pulling ao mesmo tempo — cada um com seu próprio plano e versão.",
  "finder.newPlan": "Novo plano",
  "finder.filter.basin": "Bacia",
  "finder.filter.field": "Campo",
  "finder.filter.pad": "Pad",
  "finder.filter.well": "Poço / UWI",
  "finder.filter.allBasin": "Toda bacia",
  "finder.filter.allField": "Todo campo",
  "finder.filter.allPad": "Todo pad",
  "finder.filter.wellPlaceholder": "Buscar por nome de poço ou UWI…",
  "finder.filter.eventType": "Tipo de evento",
  "finder.clear": "Limpar",
  "finder.clearAll": "Limpar todos os filtros",
  "finder.count.wells": "poços",
  "finder.count.events": "eventos",
  "finder.col.well": "Poço",
  "finder.col.location": "Bacia / Campo",
  "finder.col.pad": "Pad",
  "finder.col.events": "Eventos",
  "finder.col.planStatus": "Situação do plano",
  "finder.col.actions": "Ações",
  "finder.empty": "Nenhum resultado para os filtros atuais.",
  "finder.action.open": "Abrir",
  "finder.action.newPlan": "Novo plano",
  "finder.action.clone": "Clonar plano",
  "finder.action.newVersion": "Nova versão",
  "finder.source":
    "Dados: registro de poços/wellbore Oxbo-M + event_codes (letra + convencional) · plano de registro Oxbo-P. Letra de evento via tipo_evento_enrichment — mesmo mapa do Aura AFE-Design.",
  "wizard.title": "Novo plano",
  "wizard.step.well": "Poço",
  "wizard.step.eventType": "Tipo de evento",
  "wizard.step.confirm": "Confirmar",
  "wizard.searchWell": "Buscar poço",
  "wizard.searchWellPlaceholder": "Nome de poço, UWI, pad ou campo…",
  "wizard.pickWell": "Selecionar poço",
  "wizard.noMatch": "Sem correspondências.",
  "wizard.chosenWell": "Poço",
  "wizard.eventTypePrompt": "Tipo de evento — selecione e confirme convencional / não convencional",
  "wizard.note":
    "Um plano será criado em Rascunho v1. Você poderá editar objetivo, programa, hole plan, casing e custo no Plan Workspace imediatamente.",
  "wizard.sum.well": "Poço",
  "wizard.sum.uwi": "UWI",
  "wizard.sum.eventType": "Tipo de evento",
  "wizard.sum.conv": "Convencional / Não conv.",
  "wizard.sum.afe": "AFE (atribuído ao criar)",
  "wizard.sum.afeAuto": "gerado ao criar",
  "wizard.back": "Voltar",
  "wizard.cancel": "Cancelar",
  "wizard.next": "Continuar",
  "wizard.create": "Criar plano (Rascunho v1)",
  "wizard.creating": "Criando…",
  "cost.title": "Plano de custos · Linhas AFE",
  "cost.lede":
    "Tela de autoria — grade editável WBS (passo de programa) × CBS (conta). As mudanças são salvas por linha; publicar o plano o envia ao Oxbo-Z como linha de base.",
  "cost.boundary":
    "O Oxbo-W autora o PLANO: orçamento, método de apropriação, unidade, quantidade planejada e tarifa. Comprometido, apropriado (devengado) e real vêm do motor de apropriação e são somente leitura no Oxbo-Z — não são editáveis aqui.",
  "cost.save": "Salvar alterações",
  "cost.saving": "Salvando…",
  "cost.addLine": "+ Adicionar linha AFE",
  "cost.empty": "Ainda não há linhas AFE — use “Adicionar linha AFE” para começar.",
  "cost.ctx.well": "Poço",
  "cost.ctx.afe": "AFE",
  "cost.ctx.total": "orçamento autorizado",
  "cost.rail.title": "Sequência do programa",
  "cost.rail.hint": "Selecione um passo para filtrar as linhas AFE dessa etapa.",
  "cost.step.all": "Todos os passos",
  "cost.step.allMeta": "{n} linhas",
  "cost.step.global": "Global (sem passo)",
  "cost.grid.title": "Linhas AFE",
  "cost.grid.subAll": "— todos os passos do programa",
  "cost.col.account": "Conta (CoA · Oxbo-M)",
  "cost.col.description": "Descrição",
  "cost.col.programStep": "Passo de programa",
  "cost.col.accrual": "Método de apropriação",
  "cost.col.unit": "Unidade",
  "cost.col.qty": "Qtd.",
  "cost.col.rate": "Tarifa",
  "cost.col.budget": "Orçamento",
  "cost.account.placeholder": "Conta…",
  "cost.unit.placeholder": "un",
  "cost.budget.derived": "Orçamento = quantidade planejada × tarifa (autoritativo do servidor)",
  "cost.total.all": "Total — todas as linhas",
  "cost.total.step": "Total — este passo",
  "cost.phase.title": "Custo por fase (orçamento AFE)",
  "cost.accrual.avance_etapas": "Por etapas (avanço)",
  "cost.accrual.devengado": "Apropriado (devengado)",
  "cost.accrual.adjusted_cost": "Custo ajustado",
  "cost.accrual.straight_line": "Linha reta",
  "program.title": "Programa operativo",
  "program.badge": "Autoria · Engenheiro de perfuração / Planejador",
  "program.save": "Salvar programa",
  "program.saving": "Salvando…",
  "program.note":
    "Este é o programa operativo — a sequência ordenada de fases que governa a curva do plano (profundidade vs. dias) lida pelo Oxbo-Z e pelo reconciliador. Reordene para mudar o eixo de dias; o tempo acumulado e a curva são recalculados a cada alteração.",
  "program.ctx.well": "Poço",
  "program.ctx.event": "Evento",
  "program.ctx.afe": "AFE",
  "program.kpi.phases": "Fases em sequência",
  "program.kpi.totalTime": "Tempo total (acum.)",
  "program.kpi.dol": "DOL total",
  "program.kpi.finalDepth": "Profundidade final (MD)",
  "program.kpi.amount": "Valor total",
  "program.grid.title": "Sequência de fases",
  "program.grid.hint": "1..N, reordene com ↑ / ↓",
  "program.addPhase": "Adicionar fase",
  "program.empty": "Ainda não há fases — adicione a primeira fase para construir a curva do plano.",
  "program.total": "Total",
  "program.accHint":
    "Tempo acum. (h) é a soma corrente de Tempo (h) na ordem da Sequência — o eixo X da curva do plano (÷24 = dias decorridos). Editar Tempo, DOL ou Profundidade até atualiza a curva abaixo.",
  "program.phasePlaceholder": "Fase…",
  "program.descPlaceholder": "Descrição da atividade…",
  "program.col.seq": "Seq.",
  "program.col.phase": "Fase",
  "program.col.description": "Descrição / código de atividade",
  "program.col.depthFrom": "Prof. de (m)",
  "program.col.depthUntil": "Prof. até (m)",
  "program.col.time": "Tempo (h)",
  "program.col.dol": "DOL (d)",
  "program.col.pct": "% avanço acum.",
  "program.col.amount": "Valor (US$)",
  "program.col.accTime": "Tempo acum. (h)",
  "program.curve.title": "Curva do plano — profundidade vs. dias",
  "program.curve.sub": "Deriva de Sequência × Tempo acum. (h)/24 × Profundidade até",
  "program.curve.xLabel": "Dias acumulados (Tempo acum. h ÷ 24)",
  "program.curve.yLabel": "Profundidade MD (m)",
  "program.curve.aria":
    "Curva do plano mostrando profundidade medida acumulada contra dias decorridos, um vértice por fase na ordem da sequência.",
  "program.curve.legend": "Curva do plano",
  "program.footnote":
    "O programa operativo se adapta ao tipo de evento (perfuração / completação / workover). Para eventos de completação o eixo de profundidade pode ser plano ou decrescente em algumas fases (canhoneio, flowback) — a curva permanece monótona no eixo de dias, não necessariamente em profundidade líquida quando a fase não perfura.",
  // W6 — trajetória
  "traj.title": "Plano de trajetória",
  "traj.badge": "Autoria · Engenheiro direcional / Planejador",
  "traj.save": "Salvar + calcular",
  "traj.computing": "Calculando…",
  "traj.note":
    "Estações de survey planejadas — MD · inclinação · azimute são editáveis; TVD · N/S · E/W · DLS são calculados por curvatura mínima no servidor (somente leitura, provenance = computed). Ao editar, as colunas calculadas são limpas até o próximo salvamento.",
  "traj.computeHint":
    "Método: curvatura mínima entre estações consecutivas. O servidor retorna TVD / N-S / E-W / DLS; as duas vistas e os KPIs são redesenhados com o que ele retorna.",
  "traj.computed": "calculado",
  "traj.kop": "KOP",
  "traj.ctx.well": "Poço",
  "traj.ctx.event": "Evento",
  "traj.ctx.afe": "AFE",
  "traj.kpi.stations": "Estações de survey",
  "traj.kpi.finalMd": "MD final (planejada)",
  "traj.kpi.targetTvd": "TVD alvo",
  "traj.kpi.kop": "KOP (início da curva)",
  "traj.kpi.displacement": "Deslocamento lateral (horiz.)",
  "traj.kpi.maxDls": "DLS máx. planejado",
  "traj.grid.title": "Estações de survey planejadas",
  "traj.grid.hint": "vertical → curva → lateral, ordenadas por MD",
  "traj.addStation": "Adicionar estação de survey",
  "traj.empty":
    "Ainda não há estações — adicione a estação de superfície (MD 0) para iniciar o plano.",
  "traj.col.segment": "Segmento",
  "traj.col.md": "MD (m)",
  "traj.col.inc": "Inclinação (°)",
  "traj.col.azi": "Azimute (°)",
  "traj.col.tvd": "TVD (m)",
  "traj.col.ns": "N/S (m)",
  "traj.col.ew": "E/W (m)",
  "traj.col.dls": "DLS (°/30m)",
  "traj.seg.vertical": "Vertical",
  "traj.seg.build": "Curva (build)",
  "traj.seg.lateral": "Lateral",
  "traj.section.title": "Seção vertical",
  "traj.section.sub": "TVD vs. deslocamento horizontal",
  "traj.section.xLabel": "Deslocamento horizontal (m)",
  "traj.section.yLabel": "TVD (m)",
  "traj.section.aria":
    "Seção vertical do poço — profundidade vertical verdadeira contra deslocamento horizontal, colorida por segmento (vertical, curva, lateral).",
  "traj.section.empty": "Ainda não há TVD calculado — salve as estações para calcular a seção.",
  "traj.plan.title": "Vista em planta — N/S vs. E/W",
  "traj.plan.sub": "vista superior, norte acima",
  "traj.plan.xLabel": "E/W (m)",
  "traj.plan.yLabel": "N/S (m)",
  "traj.plan.aria":
    "Vista em planta do poço — coordenada norte contra leste a partir da boca do poço na origem, colorida por segmento.",
  "traj.plan.empty":
    "Ainda não há N/E calculado — salve as estações para calcular a vista em planta.",
  "traj.na.title": "A trajetória não se aplica a este evento",
  "traj.na.body":
    "A trajetória de survey é planejada para eventos que perfuram um poço (perfuração / sidetrack). Eventos mais leves (pulling / workover / coiled tubing) não têm plano de trajetória.",
  "traj.footnote":
    "A trajetória planejada é a linha de base que o Oxbo-Z lê para real-vs-plano (as passagens MWD/Gyro reais se sobrepõem a ela quando a perfuração começa). O cálculo de curvatura mínima é autoritativo no servidor, de modo que o plano do W e os reais do Z coincidem no número.",
  "holePlan.title": "Designer de hole plan",
  "holePlan.badge": "Autoria · Engenheiro de perfuração / Planejador",
  "holePlan.copyPrior": "Copiar de plano anterior",
  "holePlan.save": "Salvar hole plan",
  "holePlan.saving": "Salvando…",
  "holePlan.savedAt": "Salvo {time}",
  "holePlan.note":
    "O hole plan define as seções perfuradas por diâmetro e intervalo MD; alimenta o design de revestimento (W3), o programa operativo (W5) e a curva do plano. As alterações ficam na versão rascunho até Publicar (W9).",
  "holePlan.gridTitle": "Seções do hole plan",
  "holePlan.gridHint":
    "Reordene as linhas com os controles ↑ ↓. Clique em uma seção para destacá-la no esquema. O diâmetro pode ser tomado do catálogo de brocas / hole-size do Oxbo-M.",
  "holePlan.footHint":
    "O Base MD de cada seção deve igualar o Top MD da seguinte. O comprimento é calculado automaticamente (Base − Top).",
  "holePlan.addSection": "+ Adicionar seção",
  "holePlan.empty": "Ainda não há seções — adicione a primeira seção perfurada.",
  "holePlan.noPlan": "Nenhum plano selecionado. Abra um plano no buscador primeiro.",
  "holePlan.unnamed": "Seção sem nome",
  "holePlan.selectHint": "Destacar esta seção no esquema",
  "holePlan.selectFirst": "Selecione primeiro uma linha de seção",
  "holePlan.diaCatalog": "Catálogo de diâmetro (Oxbo-M)",
  "holePlan.schematic": "Esquema do poço",
  "holePlan.schematicAria":
    "Esquema vertical do poço mostrando as seções telescópicas desde o condutor mais largo na superfície até a seção mais estreita na profundidade final, com as sapatas de revestimento na base de cada seção.",
  "holePlan.kpi.sections": "Seções",
  "holePlan.kpi.td": "MD total (TD)",
  "holePlan.kpi.dol": "DOL acumulado",
  "holePlan.kpi.cost": "Custo alvo total",
  "holePlan.col.phase": "Fase",
  "holePlan.col.section": "Seção",
  "holePlan.col.diameter": "Diâmetro (in)",
  "holePlan.col.topMd": "Top MD (m)",
  "holePlan.col.baseMd": "Base MD (m)",
  "holePlan.col.length": "Comprimento (m)",
  "holePlan.col.dol": "DOL (d)",
  "holePlan.col.targetDuration": "Duração alvo (d)",
  "holePlan.col.targetCost": "Custo alvo (US$)",
  "holePlan.ctx.well": "Poço",
  "holePlan.ctx.event": "Evento",
  "holePlan.ctx.afe": "AFE",
  "holePlan.na.title": "O hole plan não se aplica a este evento",
  "holePlan.na.body":
    "O evento selecionado ({code}) reutiliza o poço perfurado existente e não requer design de seção de furo. Este plano usa o programa operativo (W5) e o plano de custos (W4) em vez disso.",
  "holePlan.na.toProgram": "Ir para programa operativo",
  "holePlan.na.toCost": "Ir para plano de custos",
  "casing.title": "Revestimento · Design tubular",
  "casing.badge": "Autoria · Engenheiro de perfuração / Planejador",
  "casing.importHole": "Importar do hole plan",
  "casing.save": "Salvar revestimento",
  "casing.saving": "Salvando…",
  "casing.savedAt": "Salvo {time}",
  "casing.note":
    "O design de revestimento coloca uma coluna tubular em cada seção perfurada — do Condutor mais externo na superfície até a coluna de Produção mais interna no TD. O OD do revestimento vincula-se ao catálogo de tubulares do Oxbo-M; o aninhamento é validado ao editar. As alterações ficam na versão rascunho até Publicar (W9).",
  "casing.gridTitle": "Colunas de revestimento",
  "casing.gridHint":
    "A ordem é Condutor → Superfície → Intermediária → Produção (da externa à interna). Reordene as linhas com os controles ↑ ↓. Clique em uma coluna para destacá-la no esquema. O OD do revestimento é tomado do catálogo de tubulares do Oxbo-M.",
  "casing.addString": "+ Adicionar coluna",
  "casing.empty": "Ainda não há colunas de revestimento — adicione a primeira (geralmente o Condutor).",
  "casing.noPlan": "Nenhum plano selecionado. Abra um plano no buscador primeiro.",
  "casing.unnamed": "Coluna sem nome",
  "casing.selectHint": "Destacar esta coluna no esquema",
  "casing.schematic": "Esquema de revestimento",
  "casing.schematicAria":
    "Esquema concêntrico de revestimento mostrando as colunas tubulares aninhadas desde o condutor mais largo na superfície até a coluna de produção mais estreita na profundidade final, com uma sapata de revestimento na base de cada coluna.",
  "casing.ok": "OK",
  "casing.od.placeholder": "Tubular…",
  "casing.type.choose": "Tipo…",
  "casing.type.conductor": "Condutor",
  "casing.type.surface": "Superfície",
  "casing.type.intermediate": "Intermediária",
  "casing.type.production": "Produção",
  "casing.kpi.strings": "Colunas projetadas",
  "casing.kpi.deepestShoe": "Sapata mais profunda",
  "casing.kpi.productionOd": "Revestimento de produção",
  "casing.kpi.warnings": "Alertas de aninhamento",
  "casing.col.string": "Coluna",
  "casing.col.type": "Tipo",
  "casing.col.section": "Seção",
  "casing.col.hole": "Furo (in)",
  "casing.col.od": "OD revest. (in)",
  "casing.col.top": "Topo (m)",
  "casing.col.base": "Base (m)",
  "casing.col.length": "Comprimento (m)",
  "casing.col.nesting": "Aninhamento",
  "casing.warn.od_ge_hole": "OD ≥ furo",
  "casing.warn.od_ge_prev": "OD ≥ coluna anterior",
  "casing.warn.base_le_prev": "Base ≤ coluna anterior",
  "casing.nest.warnTitle": "Aninhamento inconsistente em {name}:",
  "casing.nest.warnBody":
    "o OD de revestimento selecionado não cabe dentro do furo de sua própria seção, ou não aninha dentro da coluna acima. Ajuste o tubular ou o diâmetro do furo antes de publicar.",
  "casing.nest.rule":
    "Regra de aninhamento: para cada coluna, OD do revestimento < Furo de sua seção < OD da coluna anterior, e cada coluna alcança mais profundo que a de cima. Condutor (mais externo) → Produção (mais interno).",
  "casing.ctx.well": "Poço",
  "casing.ctx.event": "Evento",
  "casing.ctx.afe": "AFE",
  "casing.na.title": "O design de revestimento não se aplica a este evento",
  "casing.na.body":
    "O evento selecionado ({code}) não corre revestimento e não requer design tubular. Este plano usa o programa operativo (W5) e o plano de custos (W4) em vez disso.",
  "casing.na.toProgram": "Ir para programa operativo",
  "casing.na.toCost": "Ir para plano de custos",
  "ws.title": "Plan Workspace",
  "ws.badge": "Autoria · Engenheiro de perfuração / Planejador",
  "ws.publish": "Publicar",
  "ws.empty":
    "Nenhum evento selecionado. Abra um poço e evento no Buscador de planos para editar seu plano.",
  "ws.ctx.well": "Poço",
  "ws.ctx.event": "Evento",
  "ws.ctx.afe": "AFE",
  "ws.note":
    "Este workspace é autoria. Objetivo e atributos são editados aqui; o programa, hole plan, casing, custos e trajetória abrem em suas próprias telas. As alterações ficam na versão rascunho até Publicar, que envia a linha base ao Oxbo-Z e emite secuencia_operativa ao Aura.",
  "ws.kpi.duration": "Duração alvo",
  "ws.kpi.cost": "Custo alvo",
  "ws.kpi.finalMd": "Profundidade final (MD)",
  "ws.kpi.rig": "Sonda planejada",
  "ws.tabs.aria": "Datasets de projeto do plano",
  "ws.tab.objetivo": "Objetivo",
  "ws.tab.programa": "Programa",
  "ws.tab.holePlan": "Hole plan",
  "ws.tab.casing": "Casing",
  "ws.tab.cost": "Plano de custos",
  "ws.tab.atributos": "Atributos",
  "ws.tab.fractura": "Fratura / Etapas",
  "ws.objetivo.title": "Objetivo planejado",
  "ws.objetivo.empty": "Ainda não há atributos de objetivo.",
  "ws.atributos.title": "Atributos",
  "ws.atributos.empty": "Ainda não há atributos.",
  "ws.switcher.aria": "Trocar evento / plano",
  "ws.switcher.heading": "{well} · eventos deste poço",
  "ws.curve.title": "Curva de plano — profundidade vs. dias",
  "ws.curve.sub": "MD (m) acumulado por fase",
  "ws.curve.aria":
    "Curva de plano mostrando a profundidade medida acumulada contra os dias decorridos, um marcador por fase do programa.",
  "ws.curve.xAxis": "Dias decorridos (DOL acumulado)",
  "ws.curve.yAxis": "MD (m)",
  "ws.curve.series": "Plano de profundidade (versão atual)",
  "ws.version.title": "Versão atual",
  "ws.version.editedBy": "Editado por {actor}",
  "ws.version.count": "{n} versão(ões) em registro",
  "ws.version.none": "Ainda não há plano neste evento — inicie um no Buscador de planos.",
  "ws.version.history": "Histórico de versões",
  "ws.publishCard.title": "Publicar plano de registro",
  "ws.publishCard.body":
    "Publicar envia esta versão como linha base ao Oxbo-Z (plano-vs-real) e emite o payload secuencia_operativa ao Aura (AFE / programa / custos / hole plan / casing / atributos).",
  "ws.publishCard.action": "Publicar no Oxbo-Z + Aura",
  "publish.title": "Publicar / handoff",
  "publish.badge": "Plano de registro · linha base Oxbo-Z + Aura",
  "publish.note":
    "Publicar transforma esta versão no plano de registro: torna-se a linha base plano-vs-real que o Oxbo-Z lê, e é emitida ao Aura em substituição ao sistema incumbente AFE-Design. Revise o checklist de validação e o diff antes de continuar — os itens bloqueantes desabilitam a publicação.",
  "publish.ctx.well": "Poço",
  "publish.ctx.event": "Evento",
  "publish.ctx.afe": "AFE",
  "publish.checklist.title": "Checklist de validação",
  "publish.count.ok": "OK",
  "publish.count.warn": "aviso",
  "publish.count.blocking": "bloqueante",
  "publish.sev.ok": "OK",
  "publish.sev.warn": "Aviso",
  "publish.sev.blocking": "Bloqueante",
  "publish.fix": "Corrigir ↗",
  "publish.check.requiredFields": "Campos obrigatórios completos",
  "publish.check.requiredFields.ok":
    "Objetivo, programa, hole plan, casing e plano de custos têm todos os campos obrigatórios.",
  "publish.check.tvdLeMd": "TVD ≤ MD em cada seção",
  "publish.check.tvdLeMd.ok": "A TVD planejada não excede a MD final; a geometria é consistente.",
  "publish.check.monotonic": "Profundidade monotônica",
  "publish.check.monotonic.ok": "As fases do programa avançam sem sobreposição nem retrocesso em profundidade.",
  "publish.check.costBalance": "Linhas de custo balanceadas",
  "publish.check.costBalance.ok": "O plano de custos soma dentro da tolerância do objetivo autorizado.",
  "publish.check.casingNesting": "Aninhamento de casing válido",
  "publish.check.casingNesting.ok": "Cada seção do furo aninha dentro de um string de casing; o projeto é válido.",
  "publish.check.programSeq": "Sequência de programa completa",
  "publish.check.programSeq.ok": "As fases do programa estão sequenciadas 1..N sem lacunas.",
  "publish.diff.title": "Diff vs. versão publicada anteriormente",
  "publish.diff.sub": "Comparação v{prior} publicada → v{next} (a publicar) — o que Oxbo-Z e Aura recebem se você continuar.",
  "publish.diff.first": "Nenhuma versão publicada anterior — esta é a primeira publicação deste evento.",
  "publish.diff.none": "Nenhuma diferença a mostrar contra a última versão publicada.",
  "publish.rail.title": "Publicar plano",
  "publish.rail.hint":
    "Ao publicar, esta versão substitui a linha base atual no Oxbo-Z e é emitida ao Aura. A ação fica registrada no histórico de versões (W8).",
  "publish.action": "Publicar plano",
  "publish.publishing": "Publicando…",
  "publish.published": "Publicado",
  "publish.error": "Falha na publicação — tente novamente.",
  "publish.gate.blocked": "{n} item(ns) bloqueante(s) — corrija-os antes de publicar.",
  "publish.gate.notReady": "A validação ainda não está pronta.",
  "publish.gate.notApproved": "O plano está {status} — somente um plano aprovado ou bloqueado pode publicar.",
  "publish.gate.ready": "Todos os checks resolvidos — pronto para publicar.",
  "publish.done": "Plano publicado · v{version}.",
  "publish.done.z": "Linha base enviada ao Oxbo-Z.",
  "publish.done.aura": "secuencia_operativa emitida ao Aura ({n} tipos de dados).",
  "publish.targets.title": "Destinos de publicação",
  "publish.target.z": "Oxbo-Z (linha base)",
  "publish.target.z.sub": "Linha base plano-vs-real para relatório.",
  "publish.target.aura": "Aura (secuencia_operativa)",
  "publish.target.aura.sub": "Ingestão AFE-Design — substitui o sistema incumbente.",
  "versions.title": "Versões e suplementos",
  "versions.badge": "Autoria · Engenheiro de perfuração / Planejador",
  "versions.empty": "Ainda não há plano neste poço — inicie um no Buscador de planos.",
  "versions.noVersions": "Este evento ainda não tem versões de plano.",
  "versions.ctx.well": "Poço",
  "versions.ctx.event": "Evento",
  "versions.ctx.afe": "AFE",
  "versions.note":
    "O plano é append-only: cada edição publicada cria uma nova versão (v1, v2, v3…); as versões anteriores ficam somente leitura. Os suplementos AFE são novas autorizações ligadas ao plano original — não substituem a versão, ajustam o autorizado total.",
  "versions.kpi.original": "AFE original",
  "versions.kpi.approved": "Suplementos aprovados ({n})",
  "versions.kpi.pending": "Suplementos em trâmite ({n})",
  "versions.kpi.authorized": "Autorizado total",
  "versions.rollup.original": "original",
  "versions.rollup.approved": "{n} aprovado(s)",
  "versions.rollup.authorized": "autorizado total vigente",
  "versions.history.title": "Histórico de versões",
  "versions.history.count": "{n} versão(ões)",
  "versions.history.publishHint": "Uma versão é publicada no Oxbo-Z + Aura apenas quando chega a aprovado / bloqueado.",
  "versions.publish.ready": "Publicável",
  "versions.publish.notReady": "Ainda não publicável",
  "versions.item.published": "Publicado no Oxbo-Z + Aura",
  "versions.diff.title": "Resumo de alterações",
  "versions.diff.sub": "O que mudou entre a versão selecionada e a versão anterior.",
  "versions.diff.chip": "v{a} vs v{b}",
  "versions.diff.initial": "v{a} · versão inicial (sem comparação anterior)",
  "versions.diff.field": "Campo",
  "versions.diff.before": "Antes",
  "versions.diff.after": "Agora",
  "versions.diff.programa": "Programa operativo",
  "versions.diff.costo": "Plano de custos",
  "versions.diff.targets": "Targets",
  "versions.diff.phases": "Fases (programa)",
  "versions.diff.days": "Duração objetivo (dias)",
  "versions.diff.afeTotal": "Total de linhas AFE",
  "versions.diff.note":
    "Este diff compara datasets de projeto (programa / custo / targets) entre versões publicadas. Não inclui rascunhos intermediários não publicados.",
  "versions.supp.title": "Suplementos AFE",
  "versions.supp.request": "Solicitar suplemento",
  "versions.supp.lockedHint": "Um plano bloqueado não aceita novos suplementos.",
  "versions.supp.lockedMsg":
    "Este plano está bloqueado / publicado — novos suplementos não são aceitos. Crie uma nova versão para alterar a autorização.",
  "versions.supp.empty": "Este plano ainda não tem suplementos.",
  "versions.supp.col.seq": "Supp",
  "versions.supp.col.reason": "Motivo",
  "versions.supp.col.amount": "Valor (+US$)",
  "versions.supp.col.by": "Solicitado por",
  "versions.supp.col.status": "Status",
  "versions.supp.col.date": "Data",
  "versions.supp.state.requested": "Em revisão",
  "versions.supp.state.approved": "Aprovado",
  "versions.supp.state.rejected": "Rejeitado",
  "versions.supp.sumApproved": "Σ Suplementos aprovados",
  "versions.supp.authorizedNote": "autorizado vigente",
  "versions.supp.formTitle": "Solicitar suplemento",
  "versions.supp.formSub": "Nova autorização ligada ao AFE {afe}",
  "versions.supp.f.reason": "Motivo",
  "versions.supp.f.reasonPh":
    "Descreva a causa do suplemento (desvio de plano, condição imprevista, mudança de escopo…)",
  "versions.supp.f.amount": "Valor solicitado",
  "versions.supp.f.projected": "Autorizado total projetado",
  "versions.supp.f.hint": "O processo de aprovação é configurável por organização (D18/D24).",
  "versions.supp.f.send": "Enviar suplemento",
  "versions.supp.f.sending": "Enviando…",
  "versions.trail.title": "Trâmite de aprovação",
  "versions.trail.empty": "Nenhum suplemento em trâmite no momento.",
  "versions.trail.chip": "Supp #{seq} · em andamento",
  "versions.trail.sub":
    "Processo multietapas configurável — Campo → Escritório → JV. Cada etapa fica bloqueada até a anterior aprovar.",
  "versions.trail.stage.field": "Campo",
  "versions.trail.stage.office": "Escritório",
  "versions.trail.stage.jv": "JV",
  "versions.trail.state.approved": "Aprovado",
  "versions.trail.state.pending": "Em revisão",
  "versions.trail.state.locked": "Bloqueado",
  "versions.trail.state.rejected": "Rejeitado",
  "versions.trail.approve": "Aprovar",
  "versions.trail.reject": "Rejeitar",
  "placeholder.subtitle": "Tela estruturada — a implementação chega no fan-out W1–W9.",
  "placeholder.comingSoon": "em breve.",
  "common.loading": "Carregando…",
  "common.error": "Algo deu errado.",
  "common.retry": "Tentar novamente",
  "common.na": "—",
  "common.of": "de",
  "common.close": "Fechar",
  // W7 — linha do tempo do plano
  "timeline.title": "Linha do tempo do plano — editor multi-trilha",
  "timeline.badge": "Autoria · Engenheiro de perfuração / Planejador",
  "timeline.save": "Salvar linha do tempo",
  "timeline.saving": "Salvando…",
  "timeline.note":
    "Editor de autoria — sequencie as barras de plano (contorno) por trilha, arraste uma para reprogramá-la, ou clique numa barra para editar datas e profundidade. A faixa sombreada à direita de “Hoje” é o horizonte de antecipação. Barras reais e NPT se sobrepõem em somente leitura vindas do Oxbo-Z.",
  "timeline.ctx.well": "Poço",
  "timeline.ctx.event": "Evento",
  "timeline.zoom": "Zoom",
  "timeline.zoom.day": "Dia",
  "timeline.zoom.week": "Semana",
  "timeline.zoom.month": "Mês",
  "timeline.show": "Mostrar",
  "timeline.pa.both": "Plano + Real",
  "timeline.pa.plan": "Somente plano",
  "timeline.pa.actual": "Somente real",
  "timeline.todayLine": "Linha “Hoje”",
  "timeline.opFilter": "Filtrar linha do tempo por tipo de operação",
  "timeline.operations": "Operações",
  "timeline.op.pad": "Construção de pad",
  "timeline.op.drilling": "Perfuração",
  "timeline.op.unconv": "Completação não convencional",
  "timeline.op.completion": "Completação",
  "timeline.op.wireline": "Wireline",
  "timeline.op.ct": "Coiled tubing",
  "timeline.op.workover": "Workover",
  "timeline.legend.plan": "Plano (arrastável)",
  "timeline.legend.actual": "Real",
  "timeline.legend.npt": "NPT",
  "timeline.legend.milestone": "Marco",
  "timeline.legend.lookahead": "Horizonte look-ahead",
  "timeline.canvasAria":
    "Linha do tempo Gantt multi-trilha do plano: a trilha de equipamento de caminho crítico, as trilhas de poço e os serviços compartilhados ao longo da janela, com barras de plano tracejadas editáveis, barras reais preenchidas, um segmento NPT, losangos de marcos, uma linha Hoje e uma faixa look-ahead sombreada.",
  "timeline.empty": "Ainda sem trilhas — este plano não tem escopo de linha do tempo definido.",
  "timeline.kind.cpe": "Equipamento de caminho crítico",
  "timeline.kind.well": "Poço",
  "timeline.kind.service": "Serviço compartilhado",
  "timeline.kind.pad": "Pad",
  "timeline.grid.title": "Barras de plano por trilha",
  "timeline.grid.hint":
    "Adicione, edite, reordene (↑ ↓) ou exclua etapas de plano aqui — a linha do tempo acima reflete cada mudança. As sobreposições reais e NPT não são listadas; são somente leitura.",
  "timeline.addStep": "Adicionar etapa de plano",
  "timeline.trackEmpty": "Sem etapas de plano nesta trilha — adicione a primeira.",
  "timeline.selectRow": "Selecionar esta etapa",
  "timeline.labelPlaceholder": "Rótulo da etapa…",
  "timeline.phasePlaceholder": "Fase…",
  "timeline.col.seq": "Seq.",
  "timeline.col.label": "Rótulo",
  "timeline.col.start": "Início",
  "timeline.col.end": "Fim",
  "timeline.col.phase": "Fase",
  "timeline.editor.title": "Editar etapa de plano",
  "timeline.editor.hint": "Selecione uma barra de plano na linha do tempo ou numa grade para editá-la aqui.",
  "timeline.editor.readonly": "Esta é uma sobreposição real / NPT do Oxbo-Z — somente leitura.",
  "timeline.editor.label": "Rótulo",
  "timeline.editor.start": "Início",
  "timeline.editor.end": "Fim",
  "timeline.editor.depthFrom": "Prof. de (m)",
  "timeline.editor.depthUntil": "Prof. até (m)",
  "timeline.editor.cpe": "CPE atribuído",
  "timeline.editor.phase": "Fase",
  "timeline.editor.delete": "Excluir etapa",
  "timeline.kpi.planBars": "Barras de plano",
  "timeline.kpi.tracks": "Trilhas",
  "timeline.kpi.lookahead": "Look-ahead",
  "timeline.kpi.weeksUnit": "sem",
  "timeline.footnote":
    "A linha do tempo do plano é a baseline no nível do pad que o Oxbo-Z lê para plano-vs-real e que a publicação emite ao Aura como secuencia_operativa. As barras de plano criadas aqui são o cronograma do CPE (sonda / conjunto de fratura / CT); os reais as sobrepõem em somente leitura.",
  "common.cancel": "Cancelar",
};

export const CATALOGS: Record<Locale, Catalog> = {
  en,
  es,
  "pt-BR": ptBR,
};

/** Resolve a message, with EN fallback and simple {name} interpolation. */
export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  const raw = CATALOGS[locale][key] ?? en[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_m, name: string) =>
    name in vars ? String(vars[name]) : `{${name}}`,
  );
}
